import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  whatsappCampaignsApi,
  WhatsAppCampaign,
  WhatsAppTemplate,
  WhatsAppButtonResponseRule,
  CampaignCsvRecipient,
  CampaignSendOptions,
  CampaignReportRow,
  CampaignReportAssigneeBucket,
  TemplateSentReport,
  CrmAutomationWhatsAppReport,
  CrmAutomationReportRow,
  CrmAutomationKind,
  OpdFeedbackReport,
  FeedbackOpdAutomationConfig,
} from '@/api/whatsappCampaigns';
import { whatsappMediaApi } from '@/api/whatsappMedia';
import { appendWelcomeUtmToTemplateBody, WHATSAPP_CAMPAIGN_WELCOME_UTM } from '@/lib/whatsappUtm';
import {
  CampaignHeaderMediaPicker,
  type CampaignHeaderMediaValue,
} from '@/components/whatsapp/CampaignHeaderMediaPicker';

type CrmAutoDrillMetric =
  | 'replied'
  | 'reschedule'
  | 'call_now'
  | 'lost_revival'
  | 'sent'
  | 'delivered'
  | 'opened'
  | 'failed';

type CrmAutoDrilldown = {
  kind: 'all' | CrmAutomationKind;
  metric: CrmAutoDrillMetric;
  label: string;
};

const CRM_AUTO_DRILL_METRIC_LABELS: Record<CrmAutoDrillMetric, string> = {
  replied: 'Replied',
  reschedule: 'Reschedule',
  call_now: 'Call now',
  lost_revival: 'Lost revival',
  sent: 'Sent',
  delivered: 'Delivered',
  opened: 'Opened',
  failed: 'Failed',
};

function crmAutoRowMatchesMetric(
  row: CrmAutomationReportRow,
  metric: CrmAutoDrillMetric
): boolean {
  switch (metric) {
    case 'replied':
      return row.replied;
    case 'reschedule':
      return row.replyType === 'reschedule';
    case 'call_now':
      return row.replyType === 'call_now';
    case 'lost_revival':
      return row.replyType === 'lost_revival';
    case 'sent':
      return !row.failed;
    case 'delivered':
      return row.delivered;
    case 'opened':
      return row.opened;
    case 'failed':
      return row.failed;
    default:
      return true;
  }
}

function getTemplateHeaderFormat(metaSchema: unknown): string {
  const schema =
    metaSchema && typeof metaSchema === 'object' ? (metaSchema as Record<string, unknown>) : {};
  const stored = String(schema.headerType || '').toUpperCase();
  if (['IMAGE', 'VIDEO', 'DOCUMENT', 'TEXT'].includes(stored)) return stored;
  const components = Array.isArray(schema.components) ? schema.components : [];
  const header = components.find(
    (c: unknown) =>
      c &&
      typeof c === 'object' &&
      String((c as Record<string, unknown>).type || '').toUpperCase() === 'HEADER'
  ) as Record<string, unknown> | undefined;
  if (!header) return 'NONE';
  return String(header.format || 'TEXT').toUpperCase();
}

function templateRequiresMediaHeaderAtSend(metaSchema: unknown): boolean {
  return ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(getTemplateHeaderFormat(metaSchema));
}

function templateDefaultHeaderMedia(metaSchema: unknown): { link: string; id: string } {
  const schema =
    metaSchema && typeof metaSchema === 'object' ? (metaSchema as Record<string, unknown>) : {};
  return {
    link: String(schema.defaultHeaderMediaLink || schema.headerMediaUrl || '').trim(),
    id: String(schema.defaultHeaderMediaId || schema.headerMediaId || '').trim(),
  };
}

type TemplatePackItem = {
  name: string;
  languageCode: string;
  category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
  bodyText: string;
  bodyParamCount: number;
  description: string;
};

type TemplatePackCategory = 'AESTHETIC' | 'ORTHO' | 'MATERNITY';

const AESTHETIC_TEMPLATE_PACKS: TemplatePackItem[] = [
  { name: 'aesthetic_welcome_intro', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, welcome to {{2}}. Thanks for contacting us for aesthetic consultation. Our coordinator will assist you shortly.', bodyParamCount: 2, description: 'First touchpoint after inbound enquiry.' },
  { name: 'aesthetic_service_menu', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, we currently support Rhinoplasty, Liposuction, Hair Transplant and Gynecomastia treatments. Reply with your preferred procedure to continue.', bodyParamCount: 1, description: 'Share service catalog.' },
  { name: 'aesthetic_consultation_invite', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, Dr. {{2}} can review your case. Would you like a consultation at {{3}}?', bodyParamCount: 3, description: 'Prompt consultation booking.' },
  { name: 'rhinoplasty_info_brief', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, for Rhinoplasty, we assess goals, breathing concerns, and expected outcomes in consultation. Reply YES to book your slot.', bodyParamCount: 1, description: 'Procedure specific info.' },
  { name: 'liposuction_info_brief', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, Liposuction suitability depends on clinical assessment. We can schedule an evaluation with our specialist this week.', bodyParamCount: 1, description: 'Procedure specific info.' },
  { name: 'hair_transplant_info_brief', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, Hair Transplant planning includes graft estimate and hairline design. Share your preferred date for consultation.', bodyParamCount: 1, description: 'Procedure specific info.' },
  { name: 'gynecomastia_info_brief', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, Gynecomastia treatment options are finalized after doctor evaluation. We can help you book a consultation today.', bodyParamCount: 1, description: 'Procedure specific info.' },
  { name: 'consult_slot_options', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, available consultation slots for Dr. {{2}} are {{3}}. Reply with your preferred slot.', bodyParamCount: 3, description: 'Share available slots.' },
  { name: 'consult_booking_confirmation', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, your consultation is confirmed on {{2}} at {{3}}. Location: {{4}}. Reply if you need reschedule support.', bodyParamCount: 4, description: 'Booking confirmation.' },
  { name: 'consult_reminder_24h', languageCode: 'en', category: 'UTILITY', bodyText: 'Reminder: Hi {{1}}, your consultation with Dr. {{2}} is tomorrow at {{3}}.', bodyParamCount: 3, description: '24-hour reminder.' },
  { name: 'consult_reminder_2h', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, this is a reminder for your consultation in 2 hours at {{2}}.', bodyParamCount: 2, description: '2-hour reminder.' },
  { name: 'post_consult_plan_summary', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, as discussed, your treatment plan for {{2}} is ready. Reply PLAN to receive the next steps.', bodyParamCount: 2, description: 'Post consult follow-up.' },
  { name: 'post_consult_offer_window', languageCode: 'en', category: 'MARKETING', bodyText: 'Hi {{1}}, your special pricing window for {{2}} is valid till {{3}}. Reply BOOK to proceed.', bodyParamCount: 3, description: 'Limited-period offer.' },
  { name: 'postop_day1_checkin', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, checking in after your procedure. How are you feeling today? Reply HELP if you need urgent assistance.', bodyParamCount: 1, description: 'Day 1 postoperative check-in.' },
  { name: 'review_request_google', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, thank you for trusting {{2}}. Please share your feedback here: {{3}}', bodyParamCount: 3, description: 'Review request.' },
];

const ORTHO_TEMPLATE_PACKS: TemplatePackItem[] = [
  { name: 'ortho_welcome_intro', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, welcome to {{2}}. Thanks for contacting our orthopedic team. We will connect you shortly.', bodyParamCount: 2, description: 'First touchpoint after inbound enquiry.' },
  { name: 'ortho_service_menu', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, we provide orthopedic care for knee/hip pain, sports injuries, fractures, and joint replacement follow-ups. Reply with your main issue (knee, hip, fracture, or other) to continue.', bodyParamCount: 1, description: 'Share service catalog.' },
  { name: 'ortho_consultation_invite', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, Dr. {{2}} can review your case. Would you like a consultation at {{3}}?', bodyParamCount: 3, description: 'Prompt consultation booking.' },
  { name: 'knee_pain_info_brief', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, for knee pain our orthopedic team evaluates pain duration, walking difficulty, and mobility. Please share your age and whether you have prior X-ray/MRI reports.', bodyParamCount: 1, description: 'Procedure/condition specific info.' },
  { name: 'hip_pain_info_brief', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, for hip pain we assess movement limitation, pain triggers, and prior imaging. Please share your age and any previous reports you may have.', bodyParamCount: 1, description: 'Procedure/condition specific info.' },
  { name: 'fracture_injury_triage', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, if your injury involves severe pain, deformity, or inability to bear weight, please visit the emergency department immediately. Otherwise, share your injury date and any current reports/photos for guidance.', bodyParamCount: 1, description: 'Safety guidance message.' },
  { name: 'imaging_and_reports_request', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, to speed up your assessment, please share your latest X-ray/MRI/USG reports (photos are okay). If you don’t have reports, we will guide you on the required imaging.', bodyParamCount: 1, description: 'Request supporting documents.' },
  { name: 'preop_documents_checklist', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, before your procedure please keep your ID proof and previous medical reports ready. If you need a callback, reply to coordinate with {{2}}.', bodyParamCount: 2, description: 'Pre-procedure checklist.' },
  { name: 'post_op_physiotherapy_intro', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, after orthopedic care, physiotherapy and follow-up are important for recovery. Reply YES if you want our coordinator to share the recommended next steps.', bodyParamCount: 1, description: 'Recovery guidance.' },
  { name: 'postop_day1_checkin', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, checking in after your procedure. How are you feeling today? If you have concerns, reply HELP and our team will assist.', bodyParamCount: 1, description: 'Day 1 postoperative check-in.' },
  { name: 'postop_week_review_reminder', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, your week follow-up for orthopedic recovery is on {{2}}. Reply CONFIRM to acknowledge or RESCHEDULE if needed.', bodyParamCount: 2, description: 'Week follow-up reminder.' },
  { name: 'review_request_google', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, thank you for choosing {{2}}. Please share your feedback here: {{3}}.', bodyParamCount: 3, description: 'Review request.' },
];

const MATERNITY_TEMPLATE_PACKS: TemplatePackItem[] = [
  { name: 'maternity_welcome_intro', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, welcome to {{2}}. Thanks for messaging our maternity team. We will connect you with a doctor shortly.', bodyParamCount: 2, description: 'First touchpoint after inbound enquiry.' },
  { name: 'maternity_appointment_invite', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, Dr. {{2}} can guide you for your pregnancy visit. Reply with your preferred date/time and we will confirm.', bodyParamCount: 2, description: 'Book first pregnancy visit.' },
  { name: 'maternity_due_date_check', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, to schedule correctly please share your due date (DD/MM) and current week of pregnancy. We will coordinate next steps with {{2}}.', bodyParamCount: 2, description: 'Collect due-date context.' },
  { name: 'maternity_prenatal_reminder_24h', languageCode: 'en', category: 'UTILITY', bodyText: 'Reminder: Hi {{1}}, your prenatal checkup with Dr. {{2}} is tomorrow at {{3}}.', bodyParamCount: 3, description: '24-hour reminder.' },
  { name: 'maternity_ultrasound_booking', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, ultrasound scheduling can be coordinated by our team. Please share which scan you need (Anomaly/Follow-up) and we will arrange with {{2}}.', bodyParamCount: 2, description: 'Ultrasound booking.' },
  { name: 'maternity_labor_warning_guidance', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, if you experience severe pain, heavy bleeding, your water breaks, or decreased baby movement, please contact us immediately or visit the emergency department.', bodyParamCount: 1, description: 'Safety guidance message.' },
  { name: 'maternity_postpartum_day1_checkin', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, congratulations. How are you feeling today? If you need breastfeeding or recovery support, reply HELP and our coordinator will reach out.', bodyParamCount: 1, description: 'Postpartum Day 1 check-in.' },
  { name: 'maternity_breastfeeding_support_invite', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, our lactation support team is available. Would you like a consult with {{2}} today? Reply YES.', bodyParamCount: 2, description: 'Breastfeeding consult.' },
  { name: 'maternity_newborn_care_tips', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, here are a few newborn care tips: feed regularly, keep track of wet diapers, and seek help if baby seems unwell. Reply with any questions.', bodyParamCount: 1, description: 'Newborn care tips.' },
  { name: 'maternity_followup_day7', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, your postpartum follow-up is scheduled for {{2}}. Reply CONFIRM.', bodyParamCount: 2, description: 'Day 7 follow-up.' },
  { name: 'maternity_reschedule_support', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, if you want to reschedule your maternity appointment, tell us your preferred alternate time and we will confirm.', bodyParamCount: 1, description: 'Reschedule support.' },
  { name: 'maternity_review_request_google', languageCode: 'en', category: 'UTILITY', bodyText: 'Hi {{1}}, thank you for choosing {{2}}. Please share your feedback here: {{3}}.', bodyParamCount: 3, description: 'Review request.' },
];

const TEMPLATE_PACKS: Record<TemplatePackCategory, TemplatePackItem[]> = {
  AESTHETIC: AESTHETIC_TEMPLATE_PACKS,
  ORTHO: ORTHO_TEMPLATE_PACKS,
  MATERNITY: MATERNITY_TEMPLATE_PACKS,
};

export default function WhatsAppCampaignsPage() {
  const qc = useQueryClient();

  const AVAILABLE_CAMPAIGN_TOKENS: Array<{ token: string; meaning: string }> = [
    { token: '{{lead.patientName}}', meaning: 'Lead / patient full name' },
    { token: '{{lead.firstName}}', meaning: 'First word of patient name (for Meta “First Name”)' },
    { token: '{{lead.lastName}}', meaning: 'Rest of patient name (for Meta “Last Name”)' },
    { token: '{{lead.phone}}', meaning: 'Phone number' },
    { token: '{{lead.status}}', meaning: 'Lead status' },
    { token: '{{lead.leadSubStatus}}', meaning: 'Lead sub-status' },
    { token: '{{lead.source}}', meaning: 'Lead source' },
    { token: '{{lead.campaignName}}', meaning: 'Campaign name on the lead (before this send)' },
    { token: '{{lead.nextFollowUpAt}}', meaning: 'Next follow-up timestamp (ISO)' },
    { token: '{{hospital.name}}', meaning: 'Hospital name' },
    { token: '{{hospital.address}}', meaning: 'Hospital address' },
    { token: '{{hospital.contactNumber}}', meaning: 'Hospital contact number' },
  ];

  const detectMetaBodyParamMax = (text: string): number => {
    const s = String(text || '');
    const matches = Array.from(s.matchAll(/{{\s*(\d+)\s*}}/g)).map((m) => Number(m?.[1]));
    const max = matches.length ? Math.max(...matches.filter((n) => Number.isFinite(n))) : 0;
    return Number.isFinite(max) && max > 0 ? max : 0;
  };

  const hasLeadingOrTrailingParam = (text: string): boolean => {
    const s = String(text || '').trim();
    if (!s) return false;
    const leading = /^{{\s*\d+\s*}}/.test(s);
    const trailing = /{{\s*\d+\s*}}$/.test(s);
    return leading || trailing;
  };

  const { data: templates = [], isLoading: templatesLoading } = useQuery({
    queryKey: ['wa-templates'],
    queryFn: async () => (await whatsappCampaignsApi.listTemplates()).data.data as WhatsAppTemplate[],
    staleTime: 30_000,
  });

  const { data: campaigns = [], isLoading: campaignsLoading } = useQuery({
    queryKey: ['wa-campaigns'],
    queryFn: async () => (await whatsappCampaignsApi.listCampaigns()).data.data as WhatsAppCampaign[],
    staleTime: 15_000,
  });

  const [tplForm, setTplForm] = useState({
    name: '',
    languageCode: 'en',
    bodyParamCount: 0,
    category: '',
    description: '',
    status: 'DRAFT' as const,
  });

  const [headerMediaUrlInput, setHeaderMediaUrlInput] = useState('');

  const [metaTplForm, setMetaTplForm] = useState({
    name: '',
    languageCode: 'en',
    category: 'MARKETING',
    headerType: 'NONE' as 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT',
    headerText: '',
    headerMediaHandle: '',
    headerMediaId: '',
    headerMediaFilename: '',
    bodyText: '',
    footerText: '',
    buttonsCsv: '',
    bodyParamCount: 0,
  });
  const [metaTplParamNotes, setMetaTplParamNotes] = useState<Record<number, string>>({});
  const [metaTplFormLoadedFrom, setMetaTplFormLoadedFrom] = useState<string | null>(null);
  const [selectedPackCategory, setSelectedPackCategory] = useState<TemplatePackCategory>('AESTHETIC');
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateStatusFilter, setTemplateStatusFilter] = useState<'ALL' | 'DRAFT' | 'ACTIVE' | 'INACTIVE'>('ALL');
  const [templateCategoryFilter, setTemplateCategoryFilter] = useState<'ALL' | 'MARKETING' | 'UTILITY' | 'AUTHENTICATION'>('ALL');
  const [templateLanguageFilter, setTemplateLanguageFilter] = useState<string>('ALL');

  const createTemplateMut = useMutation({
    mutationFn: async (payload?: Partial<WhatsAppTemplate>) =>
      whatsappCampaignsApi.createTemplate(payload ?? tplForm),
    onSuccess: () => {
      toast.success('Template saved');
      setTplForm({ name: '', languageCode: 'en', bodyParamCount: 0, category: '', description: '', status: 'DRAFT' });
      qc.invalidateQueries({ queryKey: ['wa-templates'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Failed to save template'),
  });

  const syncMetaMut = useMutation({
    mutationFn: async () => (await whatsappCampaignsApi.syncTemplatesFromMeta()).data.data,
    onSuccess: (d: any) => {
      toast.success(`Synced ${d?.upserted ?? 0} template(s)`);
      qc.invalidateQueries({ queryKey: ['wa-templates'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Meta sync failed'),
  });

  const buildMetaSubmitPayload = () => {
    const detectedParams = detectMetaBodyParamMax(metaTplForm.bodyText);
    return {
      name: metaTplForm.name.trim(),
      languageCode: metaTplForm.languageCode,
      category: metaTplForm.category,
      bodyText: metaTplForm.bodyText,
      bodyParamCount: detectedParams || metaTplForm.bodyParamCount || 0,
      headerType: metaTplForm.headerType,
      headerText: metaTplForm.headerText,
      headerMediaHandle: metaTplForm.headerMediaHandle || undefined,
      headerMediaId: metaTplForm.headerMediaId || undefined,
      footerText: metaTplForm.footerText,
      buttonsCsv: metaTplForm.buttonsCsv,
    };
  };

  const applyTemplateMediaUploadResult = (d: any) => {
    setMetaTplForm((p) => ({
      ...p,
      headerMediaHandle: d?.headerMediaHandle || '',
      headerMediaId: d?.headerMediaId || '',
      headerMediaFilename: d?.filename || p.headerMediaFilename,
    }));
    if (d?.sourceUrl) {
      setHeaderMediaUrlInput(String(d.sourceUrl));
    }
    const warnings: string[] = Array.isArray(d?.warnings) ? d.warnings : [];
    if (d?.headerMediaId && !d?.headerMediaHandle) {
      toast.success(
        'WhatsApp media id saved — use for campaigns (Run campaign). Submit to Meta still needs resumable upload (WA_META_APP_ID in server .env).'
      );
    } else if (warnings.length) {
      toast.warning(warnings.join(' · '));
    } else {
      toast.success('Media uploaded — ready to submit template to Meta');
    }
  };

  const uploadTemplateMediaMut = useMutation({
    mutationFn: async (file: File) => (await whatsappCampaignsApi.uploadTemplateMedia(file)).data.data,
    onSuccess: applyTemplateMediaUploadResult,
    onError: (e: any) => {
      const status = e?.response?.status;
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        'Media upload failed. Set WA_META_APP_ID + WA_CLOUD_API_ACCESS_TOKEN on the server, or paste a public video URL under Run campaign.';
      if (status === 413) {
        toast.error(
          'File too large for browser upload (nginx 413). Upload your .mp4 to S3/CloudFront, paste the public HTTPS URL below, and click Upload from URL.'
        );
        return;
      }
      toast.error(msg);
    },
  });

  const uploadTemplateMediaFromUrlMut = useMutation({
    mutationFn: async () => {
      const url = headerMediaUrlInput.trim();
      if (/drive\.google\.com|docs\.google\.com|1drv\.ms|onedrive\.live/i.test(url)) {
        throw new Error(
          'Google Drive / OneDrive links do not work. Upload the .mp4 to S3 or CloudFront and paste a direct HTTPS URL ending in .mp4.'
        );
      }
      return (
        await whatsappCampaignsApi.uploadTemplateMediaFromUrl(url, metaTplForm.headerType)
      ).data.data;
    },
    onSuccess: applyTemplateMediaUploadResult,
    onError: (e: any) =>
      toast.error(e?.response?.data?.message || e?.message || 'Upload from URL failed'),
  });

  const createMetaTemplateMut = useMutation({
    mutationFn: async () => whatsappCampaignsApi.createMetaTemplate(buildMetaSubmitPayload()),
    onSuccess: () => {
      toast.success('Meta template request created');
      setMetaTplForm({
        name: '',
        languageCode: 'en',
        category: 'MARKETING',
        headerType: 'NONE',
        headerText: '',
        headerMediaHandle: '',
        headerMediaId: '',
        headerMediaFilename: '',
        bodyText: '',
        footerText: '',
        buttonsCsv: '',
        bodyParamCount: 0,
      });
      qc.invalidateQueries({ queryKey: ['wa-templates'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Meta template create failed'),
  });

  const createMetaTemplateAndSyncMut = useMutation({
    mutationFn: async () => whatsappCampaignsApi.createMetaTemplateAndSync(buildMetaSubmitPayload()),
    onSuccess: (res: any) => {
      const synced = res?.data?.data?.synced ?? 0;
      toast.success(`Template submitted to Meta and synced (${synced} templates refreshed)`);
      setMetaTplForm({
        name: '',
        languageCode: 'en',
        category: 'MARKETING',
        headerType: 'NONE',
        headerText: '',
        headerMediaHandle: '',
        headerMediaId: '',
        headerMediaFilename: '',
        bodyText: '',
        footerText: '',
        buttonsCsv: '',
        bodyParamCount: 0,
      });
      qc.invalidateQueries({ queryKey: ['wa-templates'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Submit + sync failed'),
  });

  const importAestheticPackMut = useMutation({
    mutationFn: async () => {
      let created = 0;
      let skipped = 0;
      const pack = TEMPLATE_PACKS[selectedPackCategory] || [];
      if (pack.length === 0) {
        throw new Error(`No templates configured yet for ${selectedPackCategory} pack`);
      }
      const existing = new Set(templates.map((t) => `${t.name.toLowerCase()}::${t.languageCode.toLowerCase()}`));
      for (const item of pack) {
        const key = `${item.name.toLowerCase()}::${item.languageCode.toLowerCase()}`;
        if (existing.has(key)) {
          skipped += 1;
          continue;
        }
        await whatsappCampaignsApi.createTemplate({
          name: item.name,
          languageCode: item.languageCode,
          category: item.category,
          description: item.description,
          bodyParamCount: item.bodyParamCount,
          status: 'DRAFT',
          metaSchema: {
            source: `${selectedPackCategory}_TEMPLATE_PACK_V1`,
            bodyText: item.bodyText,
          },
        });
        created += 1;
      }
      return { created, skipped };
    },
    onSuccess: async ({ created, skipped }) => {
      toast.success(`${selectedPackCategory} pack import complete (created: ${created}, skipped: ${skipped})`);
      await qc.invalidateQueries({ queryKey: ['wa-templates'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Pack import failed'),
  });

  const filteredTemplates = useMemo(() => {
    const q = templateSearch.trim().toLowerCase();
    return templates.filter((t) => {
      if (templateStatusFilter !== 'ALL' && t.status !== templateStatusFilter) return false;
      if (templateCategoryFilter !== 'ALL' && String(t.category || '').toUpperCase() !== templateCategoryFilter) return false;
      if (templateLanguageFilter !== 'ALL' && t.languageCode.toLowerCase() !== templateLanguageFilter.toLowerCase()) return false;
      if (!q) return true;
      return (
        t.name.toLowerCase().includes(q) ||
        String(t.category || '').toLowerCase().includes(q) ||
        String(t.description || '').toLowerCase().includes(q)
      );
    });
  }, [templates, templateSearch, templateStatusFilter, templateCategoryFilter, templateLanguageFilter]);

  const availableLanguages = useMemo(() => {
    const langs = Array.from(new Set(templates.map((t) => t.languageCode).filter(Boolean)));
    return langs.sort((a, b) => a.localeCompare(b));
  }, [templates]);

  const parseMetaSchemaTextComponent = (metaSchema: any, componentType: string): string => {
    // Our app stores either a direct `bodyText` shape (for locally saved drafts)
    // or Meta-like `components[]` structure (for synced templates).
    if (!metaSchema || typeof metaSchema !== 'object') return '';

    if (componentType === 'BODY' && typeof metaSchema.bodyText === 'string') return metaSchema.bodyText;
    if (componentType === 'HEADER' && typeof metaSchema.headerText === 'string') return metaSchema.headerText;
    if (componentType === 'FOOTER' && typeof metaSchema.footerText === 'string') return metaSchema.footerText;

    const components: any[] = Array.isArray(metaSchema.components) ? metaSchema.components : [];
    const comp = components.find((c) => String(c?.type || '').toUpperCase() === componentType) ?? null;
    if (!comp) return '';

    if (typeof comp?.text === 'string') return comp.text;
    // Some shapes put example under BODY example.body_text[0]
    const exampleBodyText = Array.isArray(comp?.example?.body_text?.[0]) ? comp.example.body_text[0] : null;
    if (exampleBodyText) return exampleBodyText.join(', ');
    const exampleAltText = comp?.example?.body_text?.[0];
    if (typeof exampleAltText === 'string') return exampleAltText;

    return '';
  };

  const parseMetaSchemaButtonsCsv = (metaSchema: any): string => {
    if (!metaSchema || typeof metaSchema !== 'object') return '';
    if (Array.isArray(metaSchema.buttons)) {
      // Our local draft save stores `buttons: string[]` in metaSchema in this file
      return metaSchema.buttons.map((b: any) => String(b)).filter(Boolean).join(', ');
    }

    const components: any[] = Array.isArray(metaSchema.components) ? metaSchema.components : [];
    const buttonsComponent = components.find((c) => String(c?.type || '').toUpperCase() === 'BUTTONS') ?? null;
    const buttons: any[] = Array.isArray(buttonsComponent?.buttons) ? buttonsComponent.buttons : [];
    const labels = buttons
      .map((b) => b?.text)
      .filter((x) => typeof x === 'string' && String(x).trim().length > 0)
      .map((x) => String(x).trim());
    return labels.join(', ');
  };

  const loadTemplateIntoBuilder = (t: WhatsAppTemplate) => {
    const metaSchema = t.metaSchema ?? null;
    const bodyText = parseMetaSchemaTextComponent(metaSchema, 'BODY');
    if (!bodyText) {
      toast.error('Selected template has no body text in metaSchema');
      return;
    }

    const headerText = parseMetaSchemaTextComponent(metaSchema, 'HEADER');
    const footerText = parseMetaSchemaTextComponent(metaSchema, 'FOOTER');

    const schemaHeader = String(metaSchema?.headerType || '').toUpperCase();
    const nextHeaderType: 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' =
      schemaHeader && ['TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT', 'NONE'].includes(schemaHeader)
        ? (schemaHeader as 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT')
        : headerText && headerText.trim().length > 0
          ? 'TEXT'
          : 'NONE';

    const nextCategoryRaw = String(t.category || '').toUpperCase();
    const nextCategory: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION' =
      nextCategoryRaw === 'UTILITY' ? 'UTILITY' : nextCategoryRaw === 'AUTHENTICATION' ? 'AUTHENTICATION' : 'MARKETING';

    setMetaTplForm({
      name: t.name,
      languageCode: t.languageCode,
      category: nextCategory,
      headerType: nextHeaderType,
      headerText: headerText || '',
      headerMediaHandle: String(metaSchema?.headerMediaHandle || ''),
      headerMediaId: String(metaSchema?.headerMediaId || ''),
      headerMediaFilename: '',
      bodyText: bodyText || '',
      footerText: footerText || '',
      buttonsCsv: parseMetaSchemaButtonsCsv(metaSchema),
      bodyParamCount: t.bodyParamCount ?? 0,
    });
    setMetaTplFormLoadedFrom(t.id);

    // Smooth-scroll back to the builder so user can immediately hit Submit.
    setTimeout(() => {
      const el = document.getElementById('wa-meta-template-builder');
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const [campForm, setCampForm] = useState({
    name: '',
    templateId: '',
    textBody: '',
    bodyParamsCsv: '',
    audienceMode: 'FILTER' as 'FILTER' | 'CSV',
    csvRecipients: [] as CampaignCsvRecipient[],
    csvFileName: '',
    defaultSource: 'WHATSAPP_CAMPAIGN',
    createMissingLeads: false,
    headerMediaLink: '',
    headerMediaId: '',
    libraryMediaId: '',
    urlButtonParam: '',
    statusesCsv: 'NEW,CONTACTED,INTERESTED,FOLLOW_UP',
    subStatusesCsv: '',
    sourcesCsv: '',
    campaignNamesCsv: '',
    assignedToIdsCsv: '',
    isSurgeryCase: 'ANY' as 'ANY' | 'YES' | 'NO',
    createdFrom: '',
    createdTo: '',
    excludeStatusesCsv: '',
    excludeSubStatusesCsv: '',
    excludePhones: [] as string[],
    excludeCsvFileName: '',
  });

  const [csvParseHint, setCsvParseHint] = useState<string | null>(null);
  const [excludeCsvHint, setExcludeCsvHint] = useState<string | null>(null);

  function fileLabelFromCount(n: number) {
    return `${n} recipient${n === 1 ? '' : 's'}`;
  }

  const parseAudienceCsvMut = useMutation({
    mutationFn: async (file: File) => (await whatsappCampaignsApi.parseAudienceCsv(file)).data.data,
    onSuccess: (d: any) => {
      const recipients = (d?.recipients || []) as CampaignCsvRecipient[];
      if (!recipients.length) {
        const err0 = d?.errors?.[0]?.reason;
        const hint =
          err0 ||
          'No valid phone numbers found. CSV must have a header row with a "phone" column (or use Download CSV template).';
        setCsvParseHint(hint);
        setCampForm((p) => ({ ...p, audienceMode: 'CSV', csvRecipients: [], csvFileName: '' }));
        toast.error(hint);
        return;
      }
      setCsvParseHint(null);
      setCampForm((p) => ({
        ...p,
        audienceMode: 'CSV',
        csvRecipients: recipients,
        csvFileName: fileLabelFromCount(recipients.length),
      }));
      if (d?.truncated) {
        toast.warning(
          `Loaded ${recipients.length.toLocaleString()} numbers (max ${Number(d.maxRows || 50000).toLocaleString()}). Extra rows were not included.`
        );
      } else if (d?.errors?.length) {
        toast.warning(`Loaded ${recipients.length.toLocaleString()} numbers; ${d.errors.length} row(s) skipped`);
      } else {
        toast.success(`Loaded ${recipients.length.toLocaleString()} phone numbers from CSV`);
      }
    },
    onError: (e: any) => {
      const hint = e?.response?.data?.message || 'CSV parse failed — is the API updated on the server?';
      setCsvParseHint(hint);
      toast.error(hint);
    },
  });

  const parseExcludeCsvMut = useMutation({
    mutationFn: async (file: File) => (await whatsappCampaignsApi.parseAudienceCsv(file)).data.data,
    onSuccess: (d: any) => {
      const recipients = (d?.recipients || []) as CampaignCsvRecipient[];
      const phones = [
        ...new Set(
          recipients
            .map((r) => String(r.phone || '').replace(/\D/g, '').slice(-10))
            .filter((p) => p.length === 10)
        ),
      ];
      if (!phones.length) {
        const hint = d?.errors?.[0]?.reason || 'No valid exclusion phones found in CSV.';
        setExcludeCsvHint(hint);
        setCampForm((p) => ({ ...p, excludePhones: [], excludeCsvFileName: '' }));
        toast.error(hint);
        return;
      }
      setExcludeCsvHint(null);
      setCampForm((p) => ({
        ...p,
        excludePhones: phones,
        excludeCsvFileName: `${phones.length} excluded phone${phones.length === 1 ? '' : 's'}`,
      }));
      toast.success(`Loaded ${phones.length.toLocaleString()} exclusion phone numbers`);
    },
    onError: (e: any) => {
      const hint = e?.response?.data?.message || 'Exclusion CSV parse failed';
      setExcludeCsvHint(hint);
      toast.error(hint);
    },
  });

  const createCampaignBlockedReason = useMemo(() => {
    if (!campForm.name.trim()) return 'Enter a campaign name';
    if (!campForm.templateId && !campForm.textBody.trim()) return 'Select a template or enter a text message';
    if (campForm.audienceMode === 'CSV' && campForm.csvRecipients.length === 0) {
      return csvParseHint || 'Upload a CSV and wait for "Loaded N phone numbers" before creating';
    }
    return null;
  }, [campForm, csvParseHint]);

  const { data: buttonRules = [], refetch: refetchButtonRules } = useQuery({
    queryKey: ['wa-button-rules'],
    queryFn: async () =>
      (await whatsappCampaignsApi.listButtonResponseRules()).data.data as WhatsAppButtonResponseRule[],
    staleTime: 30_000,
  });

  const [buttonRuleForm, setButtonRuleForm] = useState({
    buttonLabel: '',
    leadStatus: 'INTERESTED',
    leadSubStatus: 'WANTS_CALL_BACK',
    priority: 10,
  });

  const seedButtonRulesMut = useMutation({
    mutationFn: async () => whatsappCampaignsApi.seedButtonResponseRules(),
    onSuccess: () => {
      toast.success('Default button rules added');
      refetchButtonRules();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Seed failed'),
  });

  const createButtonRuleMut = useMutation({
    mutationFn: async () =>
      whatsappCampaignsApi.createButtonResponseRule({
        name: `Button: ${buttonRuleForm.buttonLabel}`,
        buttonLabel: buttonRuleForm.buttonLabel,
        leadStatus: buttonRuleForm.leadStatus,
        leadSubStatus: buttonRuleForm.leadSubStatus,
        priority: buttonRuleForm.priority,
        isActive: true,
      }),
    onSuccess: () => {
      toast.success('Button rule saved');
      setButtonRuleForm({
        buttonLabel: '',
        leadStatus: 'INTERESTED',
        leadSubStatus: 'WANTS_CALL_BACK',
        priority: 10,
      });
      refetchButtonRules();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Failed to save rule'),
  });

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === campForm.templateId) || null,
    [templates, campForm.templateId]
  );

  const createCampaignMut = useMutation({
    mutationFn: async () => {
      const statuses = campForm.statusesCsv
        .split(/[,;\n]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const sources = campForm.sourcesCsv
        .split(/[,;\n]+/)
        .map((s) => s.trim())
        .filter(Boolean);

      const subStatuses = campForm.subStatusesCsv
        .split(/[,;\n]+/)
        .map((s) => s.trim())
        .filter(Boolean);

      const campaignNames = campForm.campaignNamesCsv
        .split(/[,;\n]+/)
        .map((s) => s.trim())
        .filter(Boolean);

      const assignedToIds = campForm.assignedToIdsCsv
        .split(/[,;\n]+/)
        .map((s) => s.trim())
        .filter(Boolean);

      const bodyParams = campForm.bodyParamsCsv
        .split(/[,;\n]+/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);

      const excludeStatuses = campForm.excludeStatusesCsv
        .split(/[,;\n]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const excludeSubStatuses = campForm.excludeSubStatusesCsv
        .split(/[,;\n]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const exclude =
        excludeStatuses.length || excludeSubStatuses.length || campForm.excludePhones.length
          ? {
              ...(excludeStatuses.length ? { statuses: excludeStatuses } : {}),
              ...(excludeSubStatuses.length ? { subStatuses: excludeSubStatuses } : {}),
              ...(campForm.excludePhones.length ? { phones: campForm.excludePhones } : {}),
            }
          : undefined;

      const audience =
        campForm.audienceMode === 'CSV'
          ? {
              mode: 'CSV' as const,
              csvRecipients: campForm.csvRecipients,
              defaultSource: campForm.defaultSource || 'WHATSAPP_CAMPAIGN',
              createMissingLeads: false,
              ...(exclude ? { exclude } : {}),
            }
          : {
              mode: 'FILTER' as const,
              statuses: statuses.length ? statuses : undefined,
              subStatuses: subStatuses.length ? subStatuses : undefined,
              sources: sources.length ? sources : undefined,
              campaignNames: campaignNames.length ? campaignNames : undefined,
              assignedToIds: assignedToIds.length ? assignedToIds : undefined,
              isSurgeryCase:
                campForm.isSurgeryCase === 'YES' ? true : campForm.isSurgeryCase === 'NO' ? false : undefined,
              createdFrom: campForm.createdFrom || undefined,
              createdTo: campForm.createdTo || undefined,
              ...(exclude ? { exclude } : {}),
            };

      const tplSchema = selectedTemplate?.metaSchema;
      const sendOptions: Record<string, string> = {};
      const ht = getTemplateHeaderFormat(tplSchema);
      if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(ht)) {
        sendOptions.headerType = ht;
        const tplMedia = selectedTemplate ? templateDefaultHeaderMedia(selectedTemplate.metaSchema) : { link: '', id: '' };
        const link = campForm.headerMediaLink.trim() || tplMedia.link;
        const id = campForm.headerMediaId.trim() || tplMedia.id;
        if (link) sendOptions.headerMediaLink = link;
        if (id) sendOptions.headerMediaId = id;
        if (campForm.libraryMediaId.trim()) sendOptions.libraryMediaId = campForm.libraryMediaId.trim();
      }
      if (campForm.urlButtonParam.trim()) sendOptions.urlButtonParam = campForm.urlButtonParam.trim();

      const payload: Partial<WhatsAppCampaign> = {
        name: campForm.name.trim(),
        templateId: campForm.templateId || null,
        textBody: campForm.templateId ? null : campForm.textBody,
        bodyParams: bodyParams.length ? bodyParams : null,
        audience,
        sendOptions: Object.keys(sendOptions).length ? sendOptions : null,
      };
      return whatsappCampaignsApi.createCampaign(payload);
    },
    onSuccess: () => {
      toast.success('Campaign created');
      setCampForm({
        name: '',
        templateId: '',
        textBody: '',
        bodyParamsCsv: '',
        audienceMode: 'FILTER',
        csvRecipients: [],
        csvFileName: '',
        defaultSource: 'WHATSAPP_CAMPAIGN',
        createMissingLeads: false,
        headerMediaLink: '',
        headerMediaId: '',
        libraryMediaId: '',
        urlButtonParam: '',
        statusesCsv: 'NEW,CONTACTED,INTERESTED,FOLLOW_UP',
        subStatusesCsv: '',
        sourcesCsv: '',
        campaignNamesCsv: '',
        assignedToIdsCsv: '',
        isSurgeryCase: 'ANY',
        createdFrom: '',
        createdTo: '',
        excludeStatusesCsv: '',
        excludeSubStatusesCsv: '',
        excludePhones: [],
        excludeCsvFileName: '',
      });
      setExcludeCsvHint(null);
      qc.invalidateQueries({ queryKey: ['wa-campaigns'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Failed to create campaign'),
  });

  const [activeCampaignId, setActiveCampaignId] = useState<string>('');
  const activeCampaign = useMemo(
    () => campaigns.find((c) => c.id === activeCampaignId) || null,
    [campaigns, activeCampaignId]
  );
  const activeCampaignTemplate = useMemo(
    () => templates.find((t) => t.id === activeCampaign?.templateId) || null,
    [templates, activeCampaign?.templateId]
  );
  const [runHeaderMediaLink, setRunHeaderMediaLink] = useState('');
  const [runHeaderMediaId, setRunHeaderMediaId] = useState('');
  const [runLibraryMediaId, setRunLibraryMediaId] = useState('');

  const runHeaderMediaValue: CampaignHeaderMediaValue = {
    headerMediaLink: runHeaderMediaLink,
    headerMediaId: runHeaderMediaId,
    libraryMediaId: runLibraryMediaId,
  };

  const setRunHeaderMediaValue = (next: CampaignHeaderMediaValue) => {
    setRunHeaderMediaLink(next.headerMediaLink);
    setRunHeaderMediaId(next.headerMediaId);
    setRunLibraryMediaId(next.libraryMediaId);
  };

  useEffect(() => {
    const so = activeCampaign?.sendOptions;
    const tplDefaults = activeCampaignTemplate
      ? templateDefaultHeaderMedia(activeCampaignTemplate.metaSchema)
      : { link: '', id: '' };
    setRunHeaderMediaLink(so?.headerMediaLink || tplDefaults.link || '');
    setRunHeaderMediaId(so?.headerMediaId || tplDefaults.id || '');
    setRunLibraryMediaId(String(so?.libraryMediaId || ''));
  }, [activeCampaignId, activeCampaign?.sendOptions, activeCampaignTemplate?.metaSchema]);

  const runNeedsHeaderMedia = activeCampaignTemplate
    ? templateRequiresMediaHeaderAtSend(activeCampaignTemplate.metaSchema)
    : false;
  const runHeaderFormat = activeCampaignTemplate
    ? getTemplateHeaderFormat(activeCampaignTemplate.metaSchema)
    : '';
  const runHeaderMediaReady =
    !runNeedsHeaderMedia || Boolean(runHeaderMediaLink.trim() || runHeaderMediaId.trim());

  const saveTemplateDefaultHeaderMut = useMutation({
    mutationFn: async () => {
      if (!activeCampaignTemplate?.name) return null;
      if (runLibraryMediaId.trim()) {
        return (
          await whatsappMediaApi.saveTemplateDefault(
            activeCampaignTemplate.name,
            runLibraryMediaId.trim()
          )
        ).data;
      }
      const link = runHeaderMediaLink.trim();
      const id = runHeaderMediaId.trim();
      if (!link && !id) throw new Error('Select library media or enter a URL / media id first');
      const merged = {
        ...(activeCampaignTemplate.metaSchema && typeof activeCampaignTemplate.metaSchema === 'object'
          ? activeCampaignTemplate.metaSchema
          : {}),
        defaultHeaderMediaLink: link || undefined,
        defaultHeaderMediaId: id || undefined,
      };
      return (
        await whatsappCampaignsApi.updateTemplate(activeCampaignTemplate.id, {
          metaSchema: merged,
        })
      ).data.data;
    },
    onSuccess: () => {
      toast.success('Saved as default header for this template');
      qc.invalidateQueries({ queryKey: ['wa-templates'] });
      qc.invalidateQueries({ queryKey: ['wa-media-library'] });
    },
    onError: (e: any) =>
      toast.error(e?.message || e?.response?.data?.message || 'Could not save default'),
  });

  const previewAudienceMut = useMutation({
    mutationFn: async () =>
      activeCampaignId ? (await whatsappCampaignsApi.previewAudience(activeCampaignId)).data.data : null,
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Preview failed'),
  });

  const cleanupAutoLeadsMut = useMutation({
    mutationFn: async (apply: boolean) =>
      (
        await whatsappCampaignsApi.cleanupAutoLeads({
          day: new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }),
          apply,
        })
      ).data.data as {
        mode: string;
        dayIst: string;
        hospitalId?: string;
        candidates: number;
        keptWithEngagement: number;
        deleted: number;
        toDelete: Array<{ id: string; phone: string; patientName: string }>;
      },
    onSuccess: (d, apply) => {
      if (!apply) {
        toast.message(
          `Preview (${d.dayIst}): ${d.toDelete.length} of ${d.candidates} NEW WHATSAPP_CAMPAIGN leads would be removed (${d.keptWithEngagement} kept with reply/button).`
        );
        return;
      }
      if (!d.deleted) {
        toast.warning(
          `No leads deleted (${d.dayIst}). If they still show on Leads, hard-refresh and try Preview cleanup first.`
        );
        return;
      }
      toast.success(`Removed ${d.deleted} auto-created campaign leads (${d.dayIst}). Refresh the Leads page.`);
      qc.invalidateQueries({ queryKey: ['wa-campaigns'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Cleanup failed'),
  });

  const [backgroundSendingId, setBackgroundSendingId] = useState<string>('');

  const sendNowMut = useMutation({
    mutationFn: async () => {
      if (!activeCampaignId) return null;
      const sendOptions: CampaignSendOptions = {};
      if (runNeedsHeaderMedia && runHeaderFormat !== 'NONE') {
        sendOptions.headerType = runHeaderFormat;
        if (runHeaderMediaLink.trim()) sendOptions.headerMediaLink = runHeaderMediaLink.trim();
        if (runHeaderMediaId.trim()) sendOptions.headerMediaId = runHeaderMediaId.trim();
        if (runLibraryMediaId.trim()) sendOptions.libraryMediaId = runLibraryMediaId.trim();
      }
      return (
        await whatsappCampaignsApi.sendNow(
          activeCampaignId,
          5000,
          Object.keys(sendOptions).length ? sendOptions : undefined
        )
      ).data.data;
    },
    onSuccess: (d: any) => {
      if (!d) return;
      if (d.async || d.started) {
        toast.success(d.message || 'Sending started in the background…');
        setBackgroundSendingId(activeCampaignId);
        qc.invalidateQueries({ queryKey: ['wa-campaigns'] });
        return;
      }
      toast.success(`Sent: ${d.sent}, skipped: ${d.skipped}, failed: ${d.failed}`);
      qc.invalidateQueries({ queryKey: ['wa-campaigns'] });
      qc.invalidateQueries({ queryKey: ['wa-campaign-report'] });
    },
    onError: (e: any) => {
      const msg =
        e?.response?.data?.message ||
        (e?.code === 'ECONNABORTED' || e?.message?.includes('timeout')
          ? 'Request timed out — if send already started, check campaign status / report; do not spam Send.'
          : 'Send failed');
      toast.error(msg);
    },
  });

  const unstickMut = useMutation({
    mutationFn: async () => {
      if (!activeCampaignId) return null;
      return (await whatsappCampaignsApi.unstickCampaign(activeCampaignId)).data.data;
    },
    onSuccess: (d: any) => {
      setBackgroundSendingId('');
      qc.invalidateQueries({ queryKey: ['wa-campaigns'] });
      qc.invalidateQueries({ queryKey: ['wa-campaign-report'] });
      toast.success(
        d?.unstuck
          ? 'Marked finished — you can click Send now again for remaining numbers.'
          : d?.reason || 'Campaign is no longer stuck.'
      );
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Could not unstick campaign'),
  });

  useEffect(() => {
    if (!backgroundSendingId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const camp = (await whatsappCampaignsApi.getCampaign(backgroundSendingId)).data.data as {
          status?: string;
        };
        if (cancelled) return;
        const st = String(camp?.status || '');
        if (st === 'SENDING') {
          qc.invalidateQueries({ queryKey: ['wa-campaigns'] });
          return;
        }
        setBackgroundSendingId('');
        qc.invalidateQueries({ queryKey: ['wa-campaigns'] });
        qc.invalidateQueries({ queryKey: ['wa-campaign-report'] });
        if (st === 'SENT') {
          try {
            const report = (await whatsappCampaignsApi.getCampaignReport(backgroundSendingId, { page: 1, pageSize: 1 }))
              .data.data as { totals?: { sent?: number; failed?: number; skipped?: number } };
            const t = report?.totals;
            toast.success(
              t
                ? `Batch finished — sent: ${t.sent ?? 0}, failed: ${t.failed ?? 0}, skipped: ${t.skipped ?? 0}. Click Send again for remaining numbers.`
                : 'Batch finished. Click Send again for remaining numbers.'
            );
          } catch {
            toast.success('Batch finished. Click Send again for remaining numbers.');
          }
        } else if (st === 'FAILED') {
          toast.error('Campaign send failed. Check header media / Meta errors, then try again.');
        }
      } catch {
        /* keep polling */
      }
    };
    void poll();
    const timer = window.setInterval(() => void poll(), 4000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [backgroundSendingId, qc]);

  const [reportCampaignId, setReportCampaignId] = useState('');
  const [reportAssigneeFilter, setReportAssigneeFilter] = useState('');
  const [reportPage, setReportPage] = useState(1);

  const defaultTemplateSentTo = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const defaultTemplateSentFrom = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  }, []);
  const [templateSentFrom, setTemplateSentFrom] = useState(defaultTemplateSentFrom);
  const [templateSentTo, setTemplateSentTo] = useState(defaultTemplateSentTo);
  const [templateSentSearch, setTemplateSentSearch] = useState('');
  const [crmAutoFrom, setCrmAutoFrom] = useState(defaultTemplateSentFrom);
  const [crmAutoTo, setCrmAutoTo] = useState(defaultTemplateSentTo);
  const [crmAutoKind, setCrmAutoKind] = useState<
    'all' | 'DNP_RESCHEDULE' | 'FOLLOW_UP_MISSED' | 'LOST_CHECK' | 'CAMPAIGN_WELCOME'
  >('all');
  const [crmAutoSearch, setCrmAutoSearch] = useState('');
  const [crmAutoDrilldown, setCrmAutoDrilldown] = useState<CrmAutoDrilldown | null>(null);
  const crmAutoDetailRef = useRef<HTMLDivElement>(null);
  const [opdFeedbackFrom, setOpdFeedbackFrom] = useState(defaultTemplateSentFrom);
  const [opdFeedbackTo, setOpdFeedbackTo] = useState(defaultTemplateSentTo);
  const [opdFeedbackSearch, setOpdFeedbackSearch] = useState('');
  const [opdAutoEnabled, setOpdAutoEnabled] = useState(true);
  const [opdAutoTemplateName, setOpdAutoTemplateName] = useState('');
  const [opdAutoLanguage, setOpdAutoLanguage] = useState('');
  const [opdAutoSendFlowToken, setOpdAutoSendFlowToken] = useState(true);
  const [opdAutoHeader, setOpdAutoHeader] = useState<CampaignHeaderMediaValue>({
    headerMediaLink: '',
    headerMediaId: '',
    libraryMediaId: '',
  });
  const [opdAutoHydrated, setOpdAutoHydrated] = useState(false);

  const reportQuery = useQuery({
    queryKey: ['wa-campaign-report', reportCampaignId, reportAssigneeFilter, reportPage],
    queryFn: async () =>
      reportCampaignId
        ? (
            await whatsappCampaignsApi.getCampaignReport(reportCampaignId, {
              page: reportPage,
              pageSize: 50,
              assignedToId: reportAssigneeFilter || undefined,
            })
          ).data.data
        : null,
    enabled: !!reportCampaignId,
  });

  const templateSentQuery = useQuery({
    queryKey: ['wa-template-sent-report', templateSentFrom, templateSentTo],
    queryFn: async () =>
      (
        await whatsappCampaignsApi.getTemplateSentReport({
          from: templateSentFrom || undefined,
          to: templateSentTo || undefined,
        })
      ).data.data as TemplateSentReport,
  });

  const templateSentRows = useMemo(() => {
    const rows = templateSentQuery.data?.rows || [];
    const q = templateSentSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.templateName.toLowerCase().includes(q) ||
        (r.languageCode || '').toLowerCase().includes(q) ||
        (r.category || '').toLowerCase().includes(q)
    );
  }, [templateSentQuery.data?.rows, templateSentSearch]);

  const downloadReportMut = useMutation({
    mutationFn: async () => {
      if (!reportCampaignId) return;
      const res = await whatsappCampaignsApi.downloadCampaignReportCsv(
        reportCampaignId,
        reportAssigneeFilter || undefined
      );
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const name =
        reportQuery.data?.campaign?.name?.replace(/[^a-zA-Z0-9_-]+/g, '_') || 'campaign';
      a.download = `whatsapp_${name}_report.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
    onSuccess: () => toast.success('Report downloaded'),
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Download failed'),
  });

  const downloadTemplateSentMut = useMutation({
    mutationFn: async () => {
      const res = await whatsappCampaignsApi.downloadTemplateSentReportCsv({
        from: templateSentFrom || undefined,
        to: templateSentTo || undefined,
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `whatsapp_template_sent_${templateSentFrom || 'all'}_${templateSentTo || 'now'}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
    onSuccess: () => toast.success('Template sent report downloaded'),
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Download failed'),
  });

  const crmAutoQuery = useQuery({
    queryKey: ['wa-crm-automation-report', crmAutoFrom, crmAutoTo],
    queryFn: async () =>
      (
        await whatsappCampaignsApi.getCrmAutomationReport({
          from: crmAutoFrom || undefined,
          to: crmAutoTo || undefined,
        })
      ).data.data as CrmAutomationWhatsAppReport,
  });

  const applyCrmAutoDrill = (
    kind: CrmAutoDrilldown['kind'],
    metric: CrmAutoDrillMetric,
    automationLabel: string,
    count: number
  ) => {
    if (count <= 0) return;
    const label =
      kind === 'all'
        ? `${CRM_AUTO_DRILL_METRIC_LABELS[metric]} (all automations)`
        : `${CRM_AUTO_DRILL_METRIC_LABELS[metric]} · ${automationLabel}`;
    setCrmAutoDrilldown({ kind, metric, label });
    if (kind !== 'all') {
      setCrmAutoKind(kind);
    }
    setTimeout(() => {
      crmAutoDetailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const renderCrmAutoDrillCount = (
    count: number,
    kind: CrmAutoDrilldown['kind'],
    metric: CrmAutoDrillMetric,
    automationLabel: string,
    className?: string
  ) => {
    const active =
      crmAutoDrilldown?.kind === kind && crmAutoDrilldown?.metric === metric;
    if (count <= 0) {
      return <span className={`tabular-nums ${className || ''}`}>0</span>;
    }
    return (
      <button
        type="button"
        className={`tabular-nums text-left font-medium text-blue-700 hover:underline underline-offset-2 ${
          active ? 'underline decoration-2' : ''
        } ${className || ''}`}
        onClick={() => applyCrmAutoDrill(kind, metric, automationLabel, count)}
      >
        {count}
      </button>
    );
  };

  const crmAutoRows = useMemo(() => {
    const rows = crmAutoQuery.data?.rows || [];
    const q = crmAutoSearch.trim().toLowerCase();
    return rows.filter((r) => {
      if (crmAutoKind !== 'all' && r.automationKind !== crmAutoKind) return false;
      if (crmAutoDrilldown) {
        if (crmAutoDrilldown.kind !== 'all' && r.automationKind !== crmAutoDrilldown.kind) {
          return false;
        }
        if (!crmAutoRowMatchesMetric(r, crmAutoDrilldown.metric)) return false;
      }
      if (!q) return true;
      return (
        r.patientName.toLowerCase().includes(q) ||
        r.phone.includes(q) ||
        (r.source || '').toLowerCase().includes(q) ||
        (r.assignedToName || '').toLowerCase().includes(q) ||
        (r.templateName || '').toLowerCase().includes(q) ||
        (r.replyType || '').toLowerCase().includes(q) ||
        (r.error || '').toLowerCase().includes(q)
      );
    });
  }, [crmAutoQuery.data?.rows, crmAutoKind, crmAutoSearch, crmAutoDrilldown]);

  const downloadCrmAutoMut = useMutation({
    mutationFn: async () => {
      const res = await whatsappCampaignsApi.downloadCrmAutomationReportCsv({
        from: crmAutoFrom || undefined,
        to: crmAutoTo || undefined,
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `crm_whatsapp_automations_${crmAutoFrom || 'all'}_${crmAutoTo || 'now'}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
    onSuccess: () => toast.success('CRM automations report downloaded'),
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Download failed'),
  });

  const opdFeedbackQuery = useQuery({
    queryKey: ['wa-opd-feedback-report', opdFeedbackFrom, opdFeedbackTo],
    queryFn: async () =>
      (
        await whatsappCampaignsApi.getOpdFeedbackReport({
          from: opdFeedbackFrom || undefined,
          to: opdFeedbackTo || undefined,
        })
      ).data.data as OpdFeedbackReport,
  });

  const opdFeedbackRows = useMemo(() => {
    const rows = opdFeedbackQuery.data?.rows || [];
    const q = opdFeedbackSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        (r.patientName || '').toLowerCase().includes(q) ||
        r.phone.includes(q) ||
        (r.freeText || '').toLowerCase().includes(q) ||
        (r.surveyName || '').toLowerCase().includes(q) ||
        String(r.rating ?? '').includes(q)
    );
  }, [opdFeedbackQuery.data?.rows, opdFeedbackSearch]);

  const downloadOpdFeedbackMut = useMutation({
    mutationFn: async () => {
      const res = await whatsappCampaignsApi.downloadOpdFeedbackReportCsv({
        from: opdFeedbackFrom || undefined,
        to: opdFeedbackTo || undefined,
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `opd_feedback_${opdFeedbackFrom || 'all'}_${opdFeedbackTo || 'now'}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
    onSuccess: () => toast.success('OPD feedback report downloaded'),
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Download failed'),
  });

  const opdAutoQuery = useQuery({
    queryKey: ['wa-feedback-opd-automation'],
    queryFn: async () =>
      (await whatsappCampaignsApi.getFeedbackOpdAutomation()).data
        .data as FeedbackOpdAutomationConfig,
  });

  useEffect(() => {
    if (!opdAutoQuery.data || opdAutoHydrated) return;
    const cfg = opdAutoQuery.data;
    setOpdAutoEnabled(cfg.enabled !== false);
    setOpdAutoTemplateName(cfg.templateName || cfg.effective.templateName || '');
    setOpdAutoLanguage(cfg.languageCode || cfg.effective.languageCode || '');
    setOpdAutoSendFlowToken(cfg.sendFlowToken !== false);
    if (cfg.headerMedia) {
      setOpdAutoHeader({
        headerMediaLink: cfg.headerMedia.publicUrl,
        headerMediaId: '',
        libraryMediaId: cfg.headerMedia.id,
      });
    }
    setOpdAutoHydrated(true);
  }, [opdAutoQuery.data, opdAutoHydrated]);

  const saveOpdAutoMut = useMutation({
    mutationFn: async () => {
      if (!opdAutoTemplateName.trim()) {
        throw new Error('Select a Meta template name');
      }
      return (
        await whatsappCampaignsApi.saveFeedbackOpdAutomation({
          enabled: opdAutoEnabled,
          templateName: opdAutoTemplateName.trim(),
          languageCode: opdAutoLanguage.trim() || null,
          headerMediaId: opdAutoHeader.libraryMediaId || null,
          clearHeaderMedia: !opdAutoHeader.libraryMediaId,
          sendFlowToken: opdAutoSendFlowToken,
        })
      ).data.data as FeedbackOpdAutomationConfig;
    },
    onSuccess: (cfg) => {
      toast.success('OPD feedback automation saved');
      setOpdAutoHydrated(false);
      void qc.setQueryData(['wa-feedback-opd-automation'], cfg);
      void qc.invalidateQueries({ queryKey: ['wa-feedback-opd-automation'] });
    },
    onError: (e: any) =>
      toast.error(e?.message || e?.response?.data?.message || 'Failed to save automation'),
  });

  const selectedOpdAutoTemplate = useMemo(
    () => templates.find((t) => t.name === opdAutoTemplateName) || null,
    [templates, opdAutoTemplateName]
  );

  const opdAutoHeaderFormat = useMemo(() => {
    if (selectedOpdAutoTemplate) {
      const fmt = getTemplateHeaderFormat(selectedOpdAutoTemplate.metaSchema);
      if (fmt && fmt !== 'NONE') return fmt;
    }
    return 'IMAGE';
  }, [selectedOpdAutoTemplate]);

  const fmtPct = (v: number | null | undefined) =>
    v == null ? '—' : `${v}%`;

  const fmtReportTime = (iso: string | null) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
    } catch {
      return iso;
    }
  };

  return (
    <div className="thms-page-shell">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-artis-serif text-2xl font-semibold text-artis-brown-dark">WhatsApp Campaigns</h1>
          <p className="text-sm text-muted-foreground">
            Create templates, build campaigns, send to filtered leads, and track delivery.
          </p>
        </div>
      </div>

      <div className="rounded-md border border-amber-200 bg-amber-50 p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm text-amber-950">
          <p className="font-medium">Clean auto-created campaign leads</p>
          <p className="text-amber-900/90">
            Removes today&apos;s NEW <code className="text-xs">WHATSAPP_CAMPAIGN</code> leads that were
            created only because a campaign was sent — keeps anyone who replied or tapped a button.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            disabled={cleanupAutoLeadsMut.isPending}
            onClick={() => cleanupAutoLeadsMut.mutate(false)}
          >
            Preview cleanup
          </Button>
          <Button
            variant="destructive"
            size="sm"
            disabled={cleanupAutoLeadsMut.isPending}
            onClick={() => {
              if (
                !window.confirm(
                  "Delete today's NEW WHATSAPP_CAMPAIGN leads that never replied or tapped a button? This cannot be undone."
                )
              ) {
                return;
              }
              cleanupAutoLeadsMut.mutate(true);
            }}
          >
            {cleanupAutoLeadsMut.isPending ? 'Working…' : 'Remove junk leads'}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="campaigns">
        <TabsList>
          <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
          <TabsTrigger value="crm-automations">CRM automations</TabsTrigger>
          <TabsTrigger value="template-sends">Template sends</TabsTrigger>
          <TabsTrigger value="opd-feedback">OPD feedback</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="button-rules">Button → Lead status</TabsTrigger>
        </TabsList>

        <TabsContent value="campaigns">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Create campaign</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Campaign name</Label>
                    <Input value={campForm.name} onChange={(e) => setCampForm((p) => ({ ...p, name: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label>Template (recommended)</Label>
                    <Select
                      value={campForm.templateId || undefined}
                      onValueChange={(v) => setCampForm((p) => ({ ...p, templateId: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select template" />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.name} ({t.languageCode})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedTemplate && (
                      <div className="text-xs text-muted-foreground">
                        Needs {selectedTemplate.bodyParamCount} param(s). Use tokens like{' '}
                        <code className="px-1 rounded bg-slate-100">{'{{lead.patientName}}'}</code>
                      </div>
                    )}
                  </div>
                </div>

                {!campForm.templateId && (
                  <div className="space-y-2">
                    <Label>Text message (fallback)</Label>
                    <Textarea
                      value={campForm.textBody}
                      onChange={(e) => setCampForm((p) => ({ ...p, textBody: e.target.value }))}
                      placeholder="Hi {{lead.patientName}}, …"
                      rows={4}
                    />
                    <div className="text-xs text-muted-foreground">
                      Plain text may not deliver outside the 24h window. Prefer templates for campaigns.
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Body params (comma/newline separated)</Label>
                  <Textarea
                    value={campForm.bodyParamsCsv}
                    onChange={(e) => setCampForm((p) => ({ ...p, bodyParamsCsv: e.target.value }))}
                    placeholder="{{lead.firstName}}, {{lead.lastName}}, {{lead.phone}}"
                    rows={3}
                  />
                  <p className="text-xs text-muted-foreground">
                    Meta templates use positional variables (<code>{'{{1}}'}</code>, <code>{'{{2}}'}</code>
                    , …), not labels like “First Name”. Map each position here in order with THMS tokens
                    (e.g. <code>{'{{lead.firstName}}'}</code>). Values come from the CRM lead/hospital — no
                    extra Meta integration. The lead’s <strong>Campaign</strong> column is updated only when
                    they reply or tap a button on this campaign — not on send alone.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Audience</Label>
                  <Select
                    value={campForm.audienceMode}
                    onValueChange={(v: 'FILTER' | 'CSV') => setCampForm((p) => ({ ...p, audienceMode: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="FILTER">CRM filters (existing leads)</SelectItem>
                      <SelectItem value="CSV">Upload phone list (CSV)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {campForm.audienceMode === 'CSV' && (
                  <div className="rounded-md border bg-slate-50 p-3 space-y-2">
                    <div className="flex flex-wrap gap-2 items-center">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          const res = await whatsappCampaignsApi.downloadAudienceCsvTemplate();
                          const blob = new Blob([res.data], { type: 'text/csv' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = 'whatsapp_campaign_audience.csv';
                          a.click();
                          URL.revokeObjectURL(url);
                        }}
                      >
                        Download CSV template
                      </Button>
                      <Input
                        type="file"
                        accept=".csv,text/csv"
                        className="max-w-xs"
                        disabled={parseAudienceCsvMut.isPending}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) {
                            setCsvParseHint(null);
                            parseAudienceCsvMut.mutate(f);
                          }
                        }}
                      />
                      {parseAudienceCsvMut.isPending && (
                        <span className="text-xs text-teal-700">Parsing CSV…</span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Use THMS template (phone column) or Leads bulk CSV (patientName, phone, source…). Excel
                      scientific notation for phones is normalized automatically. Up to 50,000 unique numbers
                      per upload.
                    </p>
                    {campForm.csvRecipients.length > 0 && (
                      <p className="text-sm font-medium text-teal-800">
                        Loaded {campForm.csvRecipients.length} phone numbers — you can create the campaign now.
                      </p>
                    )}
                    {campForm.audienceMode === 'CSV' &&
                      campForm.csvRecipients.length === 0 &&
                      csvParseHint && (
                        <p className="text-sm text-red-700">{csvParseHint}</p>
                      )}
                    <p className="text-xs text-muted-foreground">
                      CRM leads are <strong>not</strong> created when you send. A lead appears only if
                      the person replies on WhatsApp or taps a button on the message.
                    </p>
                  </div>
                )}

                {selectedTemplate && templateRequiresMediaHeaderAtSend(selectedTemplate.metaSchema) && (
                  <CampaignHeaderMediaPicker
                    headerFormat={getTemplateHeaderFormat(selectedTemplate.metaSchema)}
                    templateName={selectedTemplate.name}
                    value={{
                      headerMediaLink: campForm.headerMediaLink,
                      headerMediaId: campForm.headerMediaId,
                      libraryMediaId: campForm.libraryMediaId,
                    }}
                    onChange={(next) =>
                      setCampForm((p) => ({
                        ...p,
                        headerMediaLink: next.headerMediaLink,
                        headerMediaId: next.headerMediaId,
                        libraryMediaId: next.libraryMediaId,
                      }))
                    }
                    showSaveDefault={false}
                  />
                )}

                <div className="space-y-2">
                  <Label>URL button param (dynamic URL templates)</Label>
                  <Input
                    value={campForm.urlButtonParam}
                    onChange={(e) => setCampForm((p) => ({ ...p, urlButtonParam: e.target.value }))}
                  />
                </div>

                {campForm.audienceMode === 'FILTER' && (
                <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Lead statuses</Label>
                    <Input
                      value={campForm.statusesCsv}
                      onChange={(e) => setCampForm((p) => ({ ...p, statusesCsv: e.target.value }))}
                      placeholder="NEW,CONTACTED,…"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Lead sources (optional)</Label>
                    <Input
                      value={campForm.sourcesCsv}
                      onChange={(e) => setCampForm((p) => ({ ...p, sourcesCsv: e.target.value }))}
                      placeholder="GOOGLE_PAID,META,…"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Lead sub-statuses (optional)</Label>
                    <Input
                      value={campForm.subStatusesCsv}
                      onChange={(e) => setCampForm((p) => ({ ...p, subStatusesCsv: e.target.value }))}
                      placeholder="CONNECTED_DETAILS_SHARED,…"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Surgery case</Label>
                    <Select value={campForm.isSurgeryCase} onValueChange={(v: any) => setCampForm((p) => ({ ...p, isSurgeryCase: v }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ANY">Any</SelectItem>
                        <SelectItem value="YES">Yes</SelectItem>
                        <SelectItem value="NO">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Campaign names (optional)</Label>
                    <Input
                      value={campForm.campaignNamesCsv}
                      onChange={(e) => setCampForm((p) => ({ ...p, campaignNamesCsv: e.target.value }))}
                      placeholder="utm_campaign values…"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Assigned To IDs (optional)</Label>
                    <Input
                      value={campForm.assignedToIdsCsv}
                      onChange={(e) => setCampForm((p) => ({ ...p, assignedToIdsCsv: e.target.value }))}
                      placeholder="staff uuid(s)…"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Created from (ISO date)</Label>
                    <Input
                      value={campForm.createdFrom}
                      onChange={(e) => setCampForm((p) => ({ ...p, createdFrom: e.target.value }))}
                      placeholder="2026-04-01T00:00:00.000Z"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Created to (ISO date)</Label>
                    <Input
                      value={campForm.createdTo}
                      onChange={(e) => setCampForm((p) => ({ ...p, createdTo: e.target.value }))}
                      placeholder="2026-04-30T23:59:59.999Z"
                    />
                  </div>
                </div>
                </>
                )}

                <div className="rounded-md border border-amber-200 bg-amber-50/60 p-3 space-y-3">
                  <div>
                    <Label className="text-sm font-semibold">Exclusion criteria (optional)</Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Numbers matching any exclusion below are skipped for both CRM filters and CSV phone
                      lists. Status/sub-status exclusions use CRM lead phones.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Exclude lead statuses</Label>
                      <Input
                        value={campForm.excludeStatusesCsv}
                        onChange={(e) => setCampForm((p) => ({ ...p, excludeStatusesCsv: e.target.value }))}
                        placeholder="CONVERTED,NOT_INTERESTED,…"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Exclude lead sub-statuses</Label>
                      <Input
                        value={campForm.excludeSubStatusesCsv}
                        onChange={(e) =>
                          setCampForm((p) => ({ ...p, excludeSubStatusesCsv: e.target.value }))
                        }
                        placeholder="ALREADY_OPERATED,…"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Exclude phones (CSV)</Label>
                    <div className="flex flex-wrap gap-2 items-center">
                      <Input
                        type="file"
                        accept=".csv,text/csv"
                        className="max-w-xs"
                        disabled={parseExcludeCsvMut.isPending}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) {
                            setExcludeCsvHint(null);
                            parseExcludeCsvMut.mutate(f);
                          }
                          e.target.value = '';
                        }}
                      />
                      {parseExcludeCsvMut.isPending && (
                        <span className="text-xs text-amber-800">Parsing exclusion CSV…</span>
                      )}
                      {campForm.excludePhones.length > 0 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setCampForm((p) => ({
                              ...p,
                              excludePhones: [],
                              excludeCsvFileName: '',
                            }));
                            setExcludeCsvHint(null);
                          }}
                        >
                          Clear exclusion CSV
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Same format as audience CSV (phone column). Excluded numbers never receive this
                      campaign.
                    </p>
                    {campForm.excludePhones.length > 0 && (
                      <p className="text-sm font-medium text-amber-900">
                        {campForm.excludeCsvFileName ||
                          `${campForm.excludePhones.length} exclusion phone(s) loaded`}
                      </p>
                    )}
                    {excludeCsvHint && <p className="text-sm text-red-700">{excludeCsvHint}</p>}
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  {createCampaignBlockedReason && (
                    <p className="text-sm text-amber-800">
                      Cannot create yet: {createCampaignBlockedReason}
                    </p>
                  )}
                  <div className="flex gap-2">
                    <Button
                      disabled={createCampaignMut.isPending || !!createCampaignBlockedReason}
                      onClick={() => createCampaignMut.mutate()}
                    >
                      {createCampaignMut.isPending ? 'Saving…' : 'Create campaign'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Run campaign</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2">
                  <Label>Select campaign</Label>
                  <Select value={activeCampaignId || undefined} onValueChange={setActiveCampaignId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose…" />
                    </SelectTrigger>
                    <SelectContent>
                      {campaigns.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {activeCampaign && (
                  <div className="rounded-md border bg-slate-50 p-3 text-sm">
                    <div className="font-medium text-slate-900">{activeCampaign.name}</div>
                    <div className="text-xs text-slate-600 mt-1">
                      Status: <span className="font-semibold">{activeCampaign.status}</span>
                      {activeCampaign._count?.messages != null && (
                        <span className="ml-2">Messages: {activeCampaign._count.messages}</span>
                      )}
                    </div>
                  </div>
                )}

                {runNeedsHeaderMedia && activeCampaignTemplate && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-2">
                    <p className="text-sm text-amber-900">
                      <span className="font-semibold">{activeCampaignTemplate.name}</span> is approved in Meta
                      with a {runHeaderFormat} header, but WhatsApp Cloud API still needs that{' '}
                      {runHeaderFormat.toLowerCase()} on <em>each send</em> — the file in Meta Manager is only the
                      approval sample, not attached automatically to campaigns.
                    </p>
                    <CampaignHeaderMediaPicker
                      headerFormat={runHeaderFormat}
                      templateName={activeCampaignTemplate.name}
                      value={runHeaderMediaValue}
                      onChange={setRunHeaderMediaValue}
                      onSaveTemplateDefault={() => saveTemplateDefaultHeaderMut.mutate()}
                      savingDefault={saveTemplateDefaultHeaderMut.isPending}
                    />
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    disabled={!activeCampaignId || previewAudienceMut.isPending}
                    onClick={() => previewAudienceMut.mutate()}
                  >
                    Preview audience
                  </Button>
                  <Button
                    disabled={
                      !activeCampaignId ||
                      sendNowMut.isPending ||
                      !runHeaderMediaReady ||
                      backgroundSendingId === activeCampaignId ||
                      activeCampaign?.status === 'SENDING'
                    }
                    title={
                      !runHeaderMediaReady
                        ? `Provide header ${runHeaderFormat} URL or media id before sending`
                        : backgroundSendingId === activeCampaignId || activeCampaign?.status === 'SENDING'
                          ? 'Batch is sending in the background…'
                          : undefined
                    }
                    onClick={() => sendNowMut.mutate()}
                  >
                    {sendNowMut.isPending ||
                    backgroundSendingId === activeCampaignId ||
                    activeCampaign?.status === 'SENDING'
                      ? 'Sending in background…'
                      : 'Send now (5000)'}
                  </Button>
                  {activeCampaign?.status === 'SENDING' && (
                    <Button
                      type="button"
                      variant="outline"
                      disabled={!activeCampaignId || unstickMut.isPending}
                      title="Use if SENDING is stuck after an API restart — does not delete already-sent messages"
                      onClick={() => {
                        if (
                          window.confirm(
                            'Mark this send as finished?\n\nUse this if the API was restarted and status is stuck on SENDING.\nAlready-sent numbers are kept; you can Send again for remaining numbers.'
                          )
                        ) {
                          unstickMut.mutate();
                        }
                      }}
                    >
                      {unstickMut.isPending ? 'Unlocking…' : 'Mark send finished (unstick)'}
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Each click starts a background batch of up to 5,000 <strong>new</strong> numbers.
                  Previously failed numbers are retried under a <strong>separate</strong> quota (also up
                  to 5,000) and do not reduce the new-number limit. Already-sent phones are skipped.
                  Transient Meta errors retry up to 3 times per number. Wait until status leaves
                  SENDING, then click again for remaining numbers. If status stays SENDING after an
                  API restart, use <strong>Mark send finished</strong>.
                </p>
                {(backgroundSendingId === activeCampaignId || activeCampaign?.status === 'SENDING') && (
                  <p className="text-xs font-medium text-amber-700">
                    Send in progress… If this has been stuck for a long time (e.g. after deploy/restart),
                    click <strong>Mark send finished</strong>, then Send again for remaining numbers.
                  </p>
                )}

                {previewAudienceMut.data && (
                  <div className="text-sm">
                    <div className="font-semibold">Audience</div>
                    <div className="text-muted-foreground">Count: {previewAudienceMut.data.count}</div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Recent campaigns</CardTitle>
            </CardHeader>
            <CardContent>
              {(campaignsLoading || templatesLoading) && <div className="text-sm text-muted-foreground">Loading…</div>}
              {!campaignsLoading && campaigns.length === 0 && (
                <div className="text-sm text-muted-foreground">No campaigns yet.</div>
              )}
              {!campaignsLoading && campaigns.length > 0 && (
                <div className="space-y-2">
                  {campaigns.slice(0, 8).map((c) => (
                    <div key={c.id} className="flex items-center justify-between gap-3 rounded-md border p-3">
                      <div className="min-w-0">
                        <div className="font-medium truncate">{c.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {c.template?.name ? `Template: ${c.template.name}` : 'Text message'}
                          {c._count?.messages != null ? ` · Messages: ${c._count.messages}` : ''}
                        </div>
                      </div>
                      <div className="text-xs font-semibold text-slate-700">{c.status}</div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reports">
          <Card>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle>Campaign delivery report</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Per-recipient sent, delivered, opened (read), and replied. Download CSV for CRM users.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  disabled={!reportCampaignId || downloadReportMut.isPending}
                  onClick={() => downloadReportMut.mutate()}
                >
                  {downloadReportMut.isPending ? 'Downloading…' : 'Download CSV'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label>Campaign</Label>
                  <Select
                    value={reportCampaignId || undefined}
                    onValueChange={(v) => {
                      setReportCampaignId(v);
                      setReportAssigneeFilter('');
                      setReportPage(1);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select campaign…" />
                    </SelectTrigger>
                    <SelectContent>
                      {campaigns.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name} ({c.status})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Filter by assigned CRM user</Label>
                  <Select
                    value={reportAssigneeFilter || '__all__'}
                    onValueChange={(v) => {
                      setReportAssigneeFilter(v === '__all__' ? '' : v);
                      setReportPage(1);
                    }}
                    disabled={!reportCampaignId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All users" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All users</SelectItem>
                      {(reportQuery.data?.summary?.byAssignee || []).map((a: CampaignReportAssigneeBucket) => (
                        <SelectItem
                          key={a.assignedToId || '__unassigned__'}
                          value={a.assignedToId || '__unassigned__'}
                        >
                          {a.assignedToName} ({a.total})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button
                    variant="outline"
                    className="w-full"
                    disabled={!reportCampaignId || reportQuery.isFetching}
                    onClick={() => reportQuery.refetch()}
                  >
                    {reportQuery.isFetching ? 'Refreshing…' : 'Refresh'}
                  </Button>
                </div>
              </div>

              {!reportCampaignId && (
                <p className="text-sm text-muted-foreground">Select a campaign to view its report.</p>
              )}

              {reportCampaignId && reportQuery.isLoading && (
                <p className="text-sm text-muted-foreground">Loading report…</p>
              )}

              {reportQuery.data?.summary && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
                    {[
                      { label: 'Total', value: reportQuery.data.summary.totals.total },
                      { label: 'Sent', value: reportQuery.data.summary.totals.sent },
                      { label: 'Delivered', value: reportQuery.data.summary.totals.delivered },
                      { label: 'Opened', value: reportQuery.data.summary.totals.read },
                      { label: 'Replied', value: reportQuery.data.summary.totals.replied },
                      { label: 'Failed', value: reportQuery.data.summary.totals.failed },
                      { label: 'Skipped', value: reportQuery.data.summary.totals.skipped },
                    ].map((s) => (
                      <div key={s.label} className="rounded-md border bg-slate-50 p-3 text-center">
                        <div className="text-xs text-muted-foreground">{s.label}</div>
                        <div className="text-lg font-semibold">{s.value}</div>
                      </div>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    <span>
                      Delivery rate:{' '}
                      <strong className="text-slate-900">
                        {fmtPct(reportQuery.data.summary.rates.deliveryPct)}
                      </strong>
                    </span>
                    <span>
                      Open rate:{' '}
                      <strong className="text-slate-900">
                        {fmtPct(reportQuery.data.summary.rates.readPct)}
                      </strong>
                    </span>
                    <span>
                      Reply rate:{' '}
                      <strong className="text-slate-900">
                        {fmtPct(reportQuery.data.summary.rates.replyPct)}
                      </strong>
                    </span>
                    {reportQuery.data.campaign.templateName && (
                      <span>
                        Template: <strong className="text-slate-900">{reportQuery.data.campaign.templateName}</strong>
                      </span>
                    )}
                  </div>

                  {reportQuery.data.summary.byAssignee.length > 0 && (
                    <div className="overflow-x-auto rounded-md border">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-100 text-left">
                          <tr>
                            <th className="p-2">Assigned user</th>
                            <th className="p-2">Total</th>
                            <th className="p-2">Sent</th>
                            <th className="p-2">Delivered</th>
                            <th className="p-2">Opened</th>
                            <th className="p-2">Replied</th>
                            <th className="p-2">Failed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportQuery.data.summary.byAssignee.map((a: CampaignReportAssigneeBucket) => (
                            <tr
                              key={a.assignedToId || '__unassigned__'}
                              className="border-t hover:bg-slate-50 cursor-pointer"
                              onClick={() => {
                                setReportAssigneeFilter(a.assignedToId || '__unassigned__');
                                setReportPage(1);
                              }}
                            >
                              <td className="p-2 font-medium">{a.assignedToName}</td>
                              <td className="p-2">{a.total}</td>
                              <td className="p-2">{a.sent}</td>
                              <td className="p-2">{a.delivered}</td>
                              <td className="p-2">{a.read}</td>
                              <td className="p-2">{a.replied}</td>
                              <td className="p-2">{a.failed}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <p className="text-xs text-muted-foreground p-2">
                        Click a row to filter the recipient list below. Use Download CSV for the full export.
                      </p>
                    </div>
                  )}

                  <div className="overflow-x-auto rounded-md border">
                    <table className="w-full text-sm min-w-[900px]">
                      <thead className="bg-slate-100 text-left">
                        <tr>
                          <th className="p-2">Patient</th>
                          <th className="p-2">Phone</th>
                          <th className="p-2">Assigned to</th>
                          <th className="p-2">Lead status</th>
                          <th className="p-2">Sent</th>
                          <th className="p-2">Delivered</th>
                          <th className="p-2">Opened</th>
                          <th className="p-2">Replied</th>
                          <th className="p-2">Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(reportQuery.data.rows as CampaignReportRow[]).map((r) => (
                          <tr key={r.id} className="border-t">
                            <td className="p-2">{r.patientName || '—'}</td>
                            <td className="p-2">{r.phone}</td>
                            <td className="p-2">{r.assignedToName || 'Unassigned'}</td>
                            <td className="p-2 text-xs">
                              {r.leadStatus || '—'}
                              {r.leadSubStatus ? ` / ${r.leadSubStatus}` : ''}
                            </td>
                            <td className="p-2">{r.sent ? fmtReportTime(r.sentAt) : '—'}</td>
                            <td className="p-2">{r.delivered ? fmtReportTime(r.deliveredAt) : '—'}</td>
                            <td className="p-2">{r.opened ? fmtReportTime(r.readAt) : '—'}</td>
                            <td className="p-2">{r.replied ? fmtReportTime(r.repliedAt) : '—'}</td>
                            <td className="p-2 text-xs text-red-700 max-w-[200px] truncate" title={r.error || ''}>
                              {r.error || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {reportQuery.data.total > reportQuery.data.pageSize && (
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-muted-foreground">
                        Page {reportQuery.data.page} · {reportQuery.data.total} recipients
                        {reportAssigneeFilter ? ' (filtered)' : ''}
                      </span>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={reportPage <= 1}
                          onClick={() => setReportPage((p) => Math.max(1, p - 1))}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={reportPage * reportQuery.data.pageSize >= reportQuery.data.total}
                          onClick={() => setReportPage((p) => p + 1)}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}

              {reportCampaignId && !reportQuery.isLoading && reportQuery.data?.total === 0 && (
                <p className="text-sm text-muted-foreground">
                  No messages logged yet. Send the campaign first; delivery updates arrive via WhatsApp webhook.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="crm-automations">
          <Card>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle>CRM automation report</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  DNP reschedule (<code className="text-xs">dnp_1st_reschedule</code>), missed follow-up (
                  <code className="text-xs">follow_up_missed</code>), Lost revival (
                  <code className="text-xs">lost_check</code>), and campaign welcome (
                  <code className="text-xs">true_artis_campaign_welcome</code>) — sent, delivered, replies, and failures.
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  disabled={crmAutoQuery.isFetching}
                  onClick={() => crmAutoQuery.refetch()}
                >
                  {crmAutoQuery.isFetching ? 'Refreshing…' : 'Refresh'}
                </Button>
                <Button
                  variant="outline"
                  disabled={downloadCrmAutoMut.isPending}
                  onClick={() => downloadCrmAutoMut.mutate()}
                >
                  {downloadCrmAutoMut.isPending ? 'Downloading…' : 'Download CSV'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="space-y-2">
                  <Label>From</Label>
                  <Input
                    type="date"
                    value={crmAutoFrom}
                    onChange={(e) => setCrmAutoFrom(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>To</Label>
                  <Input
                    type="date"
                    value={crmAutoTo}
                    onChange={(e) => setCrmAutoTo(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Automation</Label>
                  <Select
                    value={crmAutoKind}
                    onValueChange={(v) => {
                      setCrmAutoKind(v as typeof crmAutoKind);
                      setCrmAutoDrilldown(null);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All automations</SelectItem>
                      <SelectItem value="DNP_RESCHEDULE">DNP reschedule</SelectItem>
                      <SelectItem value="FOLLOW_UP_MISSED">Missed follow-up</SelectItem>
                      <SelectItem value="LOST_CHECK">Lost revival</SelectItem>
                      <SelectItem value="CAMPAIGN_WELCOME">Campaign welcome</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Search</Label>
                  <Input
                    value={crmAutoSearch}
                    onChange={(e) => setCrmAutoSearch(e.target.value)}
                    placeholder="Name, phone, source, owner…"
                  />
                </div>
              </div>

              {crmAutoQuery.data?.totals && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                  {[
                    {
                      label: 'Sent',
                      value: crmAutoQuery.data.totals.sent,
                      metric: 'sent' as const,
                    },
                    {
                      label: 'Delivered',
                      value: crmAutoQuery.data.totals.delivered,
                      metric: 'delivered' as const,
                    },
                    {
                      label: 'Opened',
                      value: crmAutoQuery.data.totals.opened,
                      metric: 'opened' as const,
                    },
                    {
                      label: 'Replied',
                      value: crmAutoQuery.data.totals.replied,
                      metric: 'replied' as const,
                    },
                    {
                      label: 'Failed',
                      value: crmAutoQuery.data.totals.failed,
                      metric: 'failed' as const,
                    },
                    {
                      label: 'Pending scheduled',
                      value: crmAutoQuery.data.totals.pendingScheduled,
                    },
                  ].map((t) => (
                    <div
                      key={t.label}
                      className={`rounded-lg border p-3 ${
                        t.metric && t.value > 0 ? 'hover:border-blue-300' : ''
                      }`}
                    >
                      <p className="text-xs text-muted-foreground">{t.label}</p>
                      {t.metric && t.value > 0 ? (
                        <button
                          type="button"
                          className={`text-xl font-semibold tabular-nums text-blue-700 hover:underline underline-offset-2 ${
                            crmAutoDrilldown?.kind === 'all' &&
                            crmAutoDrilldown.metric === t.metric
                              ? 'underline decoration-2'
                              : ''
                          }`}
                          onClick={() =>
                            applyCrmAutoDrill('all', t.metric!, 'All automations', t.value)
                          }
                        >
                          {t.value.toLocaleString()}
                        </button>
                      ) : (
                        <p className="text-xl font-semibold tabular-nums">
                          {t.value.toLocaleString()}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {crmAutoQuery.data?.byAutomation?.length ? (
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-sm min-w-[720px]">
                    <thead className="bg-slate-100 text-left">
                      <tr>
                        <th className="p-2">Automation</th>
                        <th className="p-2">Template</th>
                        <th className="p-2">Sent</th>
                        <th className="p-2">Delivered</th>
                        <th className="p-2">Opened</th>
                        <th className="p-2">Replied</th>
                        <th className="p-2">Reschedule</th>
                        <th className="p-2">Call now</th>
                        <th className="p-2">Lost revival</th>
                        <th className="p-2">Failed</th>
                        <th className="p-2">Pending</th>
                      </tr>
                    </thead>
                    <tbody>
                      {crmAutoQuery.data.byAutomation.map((b) => (
                        <tr key={b.kind} className="border-t">
                          <td className="p-2 font-medium">{b.label}</td>
                          <td className="p-2 text-xs">{b.templateName}</td>
                          <td className="p-2">
                            {renderCrmAutoDrillCount(b.sent, b.kind, 'sent', b.label)}
                          </td>
                          <td className="p-2">
                            {renderCrmAutoDrillCount(b.delivered, b.kind, 'delivered', b.label)}
                          </td>
                          <td className="p-2">
                            {renderCrmAutoDrillCount(b.opened, b.kind, 'opened', b.label)}
                          </td>
                          <td className="p-2">
                            {renderCrmAutoDrillCount(b.replied, b.kind, 'replied', b.label)}
                          </td>
                          <td className="p-2">
                            {renderCrmAutoDrillCount(
                              b.replyReschedule,
                              b.kind,
                              'reschedule',
                              b.label
                            )}
                          </td>
                          <td className="p-2">
                            {renderCrmAutoDrillCount(b.replyCallNow, b.kind, 'call_now', b.label)}
                          </td>
                          <td className="p-2">
                            {renderCrmAutoDrillCount(
                              b.replyLostRevival,
                              b.kind,
                              'lost_revival',
                              b.label
                            )}
                          </td>
                          <td className="p-2">
                            {renderCrmAutoDrillCount(
                              b.failed,
                              b.kind,
                              'failed',
                              b.label,
                              'text-red-700'
                            )}
                          </td>
                          <td className="p-2 tabular-nums">{b.pendingScheduled}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}

              {crmAutoQuery.isLoading && (
                <p className="text-sm text-muted-foreground">Loading CRM automation report…</p>
              )}

              {!crmAutoQuery.isLoading && (
                <div ref={crmAutoDetailRef} className="space-y-2 scroll-mt-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-medium text-slate-700">
                      Lead details
                      {crmAutoRows.length > 0 ? ` (${crmAutoRows.length})` : ''}
                    </p>
                    {crmAutoDrilldown ? (
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-blue-900">
                          Filter: {crmAutoDrilldown.label}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2"
                          onClick={() => setCrmAutoDrilldown(null)}
                        >
                          Clear filter
                        </Button>
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        Click a count above to show matching leads
                      </p>
                    )}
                  </div>
                  <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-sm min-w-[1100px]">
                    <thead className="bg-slate-100 text-left">
                      <tr>
                        <th className="p-2">Automation</th>
                        <th className="p-2">Patient</th>
                        <th className="p-2">Phone</th>
                        <th className="p-2">Owner</th>
                        <th className="p-2">Lead status</th>
                        <th className="p-2">Sent</th>
                        <th className="p-2">Delivered</th>
                        <th className="p-2">Opened</th>
                        <th className="p-2">Reply</th>
                        <th className="p-2">Callback</th>
                        <th className="p-2">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {crmAutoRows.map((r: CrmAutomationReportRow, idx) => (
                        <tr
                          key={`${r.leadId}-${r.sentAt}-${r.failed ? 'f' : 's'}-${idx}`}
                          className="border-t"
                        >
                          <td className="p-2 text-xs">
                            <div className="font-medium">{r.automationLabel}</div>
                            <div className="text-muted-foreground">{r.templateName}</div>
                            {r.milestone != null ? (
                              <div className="text-muted-foreground">Cadence {r.milestone}/9</div>
                            ) : null}
                          </td>
                          <td className="p-2">{r.patientName || '—'}</td>
                          <td className="p-2">{r.phone || '—'}</td>
                          <td className="p-2">{r.assignedToName || 'Unassigned'}</td>
                          <td className="p-2 text-xs">
                            {r.status || '—'}
                            {r.leadSubStatus ? ` / ${r.leadSubStatus}` : ''}
                            {r.source ? (
                              <div className="text-muted-foreground">{r.source}</div>
                            ) : null}
                          </td>
                          <td className="p-2">{r.failed ? '—' : fmtReportTime(r.sentAt)}</td>
                          <td className="p-2">
                            {r.delivered ? fmtReportTime(r.deliveredAt) : r.failed ? 'Failed' : '—'}
                          </td>
                          <td className="p-2">{r.opened ? fmtReportTime(r.readAt) : '—'}</td>
                          <td className="p-2 text-xs">
                            {r.replied ? (
                              <>
                                <div className="font-medium capitalize">
                                  {(r.replyType || 'yes').replace(/_/g, ' ')}
                                </div>
                                <div className="text-muted-foreground">
                                  {fmtReportTime(r.repliedAt)}
                                </div>
                              </>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="p-2 text-xs">
                            {r.customerCallbackAt ? fmtReportTime(r.customerCallbackAt) : '—'}
                          </td>
                          <td
                            className="p-2 text-xs text-red-700 max-w-[200px] truncate"
                            title={r.error || ''}
                          >
                            {r.error || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}

              {!crmAutoQuery.isLoading && crmAutoRows.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  {crmAutoDrilldown
                    ? 'No leads match this filter. Clear the filter or pick another count.'
                    : 'No automation sends in this date range. Messages appear after workers send templates; delivery/open update via Meta status webhooks.'}
                </p>
              )}

              {crmAutoQuery.data?.notes?.length ? (
                <ul className="text-xs text-muted-foreground list-disc pl-4 space-y-1">
                  {crmAutoQuery.data.notes.map((n) => (
                    <li key={n}>{n}</li>
                  ))}
                </ul>
              ) : null}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="template-sends">
          <Card>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle>Template send report</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Sent counts by configured template name (campaigns, notifications, feedback_opd).
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  disabled={templateSentQuery.isFetching}
                  onClick={() => templateSentQuery.refetch()}
                >
                  {templateSentQuery.isFetching ? 'Refreshing…' : 'Refresh'}
                </Button>
                <Button
                  variant="outline"
                  disabled={downloadTemplateSentMut.isPending}
                  onClick={() => downloadTemplateSentMut.mutate()}
                >
                  {downloadTemplateSentMut.isPending ? 'Downloading…' : 'Download CSV'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="space-y-2">
                  <Label>From</Label>
                  <Input
                    type="date"
                    value={templateSentFrom}
                    onChange={(e) => setTemplateSentFrom(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>To</Label>
                  <Input
                    type="date"
                    value={templateSentTo}
                    onChange={(e) => setTemplateSentTo(e.target.value)}
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Search template</Label>
                  <Input
                    value={templateSentSearch}
                    onChange={(e) => setTemplateSentSearch(e.target.value)}
                    placeholder="Name, language, category…"
                  />
                </div>
              </div>

              {templateSentQuery.data?.totals && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                  {[
                    { label: 'Templates', value: templateSentQuery.data.totals.templates },
                    { label: 'Total sent', value: templateSentQuery.data.totals.totalSent },
                    { label: 'Campaign sent', value: templateSentQuery.data.totals.campaignSent },
                    { label: 'Notification sent', value: templateSentQuery.data.totals.notificationSent },
                    { label: 'feedback_opd sent', value: templateSentQuery.data.totals.feedbackOpdSent },
                    { label: 'Logged failures', value: templateSentQuery.data.totals.loggedFailures },
                  ].map((t) => (
                    <div key={t.label} className="rounded-lg border p-3">
                      <p className="text-xs text-muted-foreground">{t.label}</p>
                      <p className="text-xl font-semibold tabular-nums">{t.value.toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              )}

              {templateSentQuery.isLoading && (
                <p className="text-sm text-muted-foreground">Loading template send report…</p>
              )}

              {!templateSentQuery.isLoading && (
                <div className="overflow-x-auto rounded border">
                  <table className="w-full text-sm min-w-[1100px]">
                    <thead className="bg-muted/40">
                      <tr className="text-left">
                        <th className="p-2 font-medium">Template</th>
                        <th className="p-2 font-medium">Lang</th>
                        <th className="p-2 font-medium text-right">Total sent</th>
                        <th className="p-2 font-medium text-right">Campaign</th>
                        <th className="p-2 font-medium text-right">Delivered</th>
                        <th className="p-2 font-medium text-right">Opened</th>
                        <th className="p-2 font-medium text-right">Replied</th>
                        <th className="p-2 font-medium text-right">Camp. failed</th>
                        <th className="p-2 font-medium text-right">Notification</th>
                        <th className="p-2 font-medium text-right">feedback_opd</th>
                        <th className="p-2 font-medium text-right">Logged fail</th>
                        <th className="p-2 font-medium">Sources</th>
                      </tr>
                    </thead>
                    <tbody>
                      {templateSentRows.map((r) => (
                        <tr key={`${r.templateName}::${r.languageCode || ''}`} className="border-t">
                          <td className="p-2">
                            <div className="font-medium">{r.templateName}</div>
                            {(r.status || r.category) && (
                              <div className="text-xs text-muted-foreground">
                                {[r.status, r.category].filter(Boolean).join(' · ')}
                              </div>
                            )}
                          </td>
                          <td className="p-2 whitespace-nowrap">{r.languageCode || '—'}</td>
                          <td className="p-2 text-right tabular-nums font-medium">
                            {r.totalSent.toLocaleString()}
                          </td>
                          <td className="p-2 text-right tabular-nums">{r.campaignSent.toLocaleString()}</td>
                          <td className="p-2 text-right tabular-nums">
                            {r.campaignDelivered.toLocaleString()}
                          </td>
                          <td className="p-2 text-right tabular-nums">{r.campaignRead.toLocaleString()}</td>
                          <td className="p-2 text-right tabular-nums">
                            {r.campaignReplied.toLocaleString()}
                          </td>
                          <td className="p-2 text-right tabular-nums">{r.campaignFailed.toLocaleString()}</td>
                          <td className="p-2 text-right tabular-nums">
                            {r.notificationSent.toLocaleString()}
                          </td>
                          <td className="p-2 text-right tabular-nums">
                            {r.feedbackOpdSent.toLocaleString()}
                          </td>
                          <td className="p-2 text-right tabular-nums">{r.loggedFailures.toLocaleString()}</td>
                          <td className="p-2 text-xs text-muted-foreground">
                            {r.sources.length ? r.sources.join(', ') : '—'}
                          </td>
                        </tr>
                      ))}
                      {templateSentRows.length === 0 && (
                        <tr>
                          <td colSpan={12} className="p-4 text-sm text-muted-foreground">
                            No templates or sends found for this range.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {!!templateSentQuery.data?.notes?.length && (
                <ul className="text-xs text-muted-foreground list-disc pl-5 space-y-1">
                  {templateSentQuery.data.notes.map((n) => (
                    <li key={n}>{n}</li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="opd-feedback">
          <div className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
                <div>
                  <CardTitle>Automation settings</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Choose the Meta template for automatic OPD feedback. Header image is only
                    sent when that template has an IMAGE header in Meta — otherwise it is ignored.
                    Changes apply immediately — no server env edit needed.
                  </p>
                </div>
                <Button
                  disabled={saveOpdAutoMut.isPending || !opdAutoTemplateName.trim()}
                  onClick={() => saveOpdAutoMut.mutate()}
                >
                  {saveOpdAutoMut.isPending ? 'Saving…' : 'Save settings'}
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {opdAutoQuery.isLoading && (
                  <p className="text-sm text-muted-foreground">Loading automation settings…</p>
                )}
                {opdAutoQuery.data && (
                  <p className="text-xs text-muted-foreground">
                    Currently sending{' '}
                    <code className="rounded bg-slate-100 px-1">
                      {opdAutoQuery.data.effective.templateName}
                    </code>{' '}
                    (source: {opdAutoQuery.data.effective.source}
                    {opdAutoQuery.data.effective.languageCode
                      ? `, lang ${opdAutoQuery.data.effective.languageCode}`
                      : ''}
                    ; header: {opdAutoQuery.data.effective.headerSource})
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-4">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={opdAutoEnabled}
                      onChange={(e) => setOpdAutoEnabled(e.target.checked)}
                    />
                    Enabled (schedule + send after OPD visit)
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={opdAutoSendFlowToken}
                      onChange={(e) => setOpdAutoSendFlowToken(e.target.checked)}
                    />
                    Send Flow token (lead id) for survey matching
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Template</Label>
                    <Select
                      value={opdAutoTemplateName || undefined}
                      onValueChange={(v) => {
                        setOpdAutoTemplateName(v);
                        const t = templates.find((x) => x.name === v);
                        if (t?.languageCode) setOpdAutoLanguage(t.languageCode);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select synced Meta template" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from(new Set(templates.map((t) => t.name)))
                          .sort((a, b) => a.localeCompare(b))
                          .map((name) => (
                            <SelectItem key={name} value={name}>
                              {name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <Input
                      value={opdAutoTemplateName}
                      onChange={(e) => setOpdAutoTemplateName(e.target.value)}
                      placeholder="Or type exact Meta template name"
                    />
                    <p className="text-xs text-muted-foreground">
                      Sync templates from the Templates tab if the list is empty. Example:{' '}
                      <code className="text-xs">feedback_opd_bd_2</code>
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Language code</Label>
                    <Input
                      value={opdAutoLanguage}
                      onChange={(e) => setOpdAutoLanguage(e.target.value)}
                      placeholder="en"
                    />
                    <p className="text-xs text-muted-foreground">
                      Must match Meta exactly. Meta label <strong>English</strong> →{' '}
                      <code className="text-xs">en</code>. Meta{' '}
                      <strong>English (US)</strong> → <code className="text-xs">en_US</code>. Do
                      not use <code className="text-xs">en_IN</code> unless Meta shows that locale.
                    </p>
                  </div>
                </div>
                <CampaignHeaderMediaPicker
                  headerFormat={opdAutoHeaderFormat}
                  templateName={opdAutoTemplateName || undefined}
                  value={opdAutoHeader}
                  onChange={setOpdAutoHeader}
                  showSaveDefault={false}
                />
              </CardContent>
            </Card>

          <Card>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle>OPD doctor feedback</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Ratings and comments from the DOCTOR FEEDBACK survey only (excludes DNP /
                  missed follow-up Reschedule replies — see CRM automations).
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  disabled={opdFeedbackQuery.isFetching}
                  onClick={() => opdFeedbackQuery.refetch()}
                >
                  {opdFeedbackQuery.isFetching ? 'Refreshing…' : 'Refresh'}
                </Button>
                <Button
                  variant="outline"
                  disabled={downloadOpdFeedbackMut.isPending}
                  onClick={() => downloadOpdFeedbackMut.mutate()}
                >
                  {downloadOpdFeedbackMut.isPending ? 'Downloading…' : 'Download CSV'}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="space-y-2">
                  <Label>From</Label>
                  <Input
                    type="date"
                    value={opdFeedbackFrom}
                    onChange={(e) => setOpdFeedbackFrom(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>To</Label>
                  <Input
                    type="date"
                    value={opdFeedbackTo}
                    onChange={(e) => setOpdFeedbackTo(e.target.value)}
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>Search</Label>
                  <Input
                    value={opdFeedbackSearch}
                    onChange={(e) => setOpdFeedbackSearch(e.target.value)}
                    placeholder="Patient, phone, rating, comment…"
                  />
                </div>
              </div>

              {opdFeedbackQuery.data?.totals && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2">
                  {[
                    { label: 'Responses', value: opdFeedbackQuery.data.totals.responses },
                    { label: 'With rating', value: opdFeedbackQuery.data.totals.withRating },
                    {
                      label: 'Avg rating',
                      value:
                        opdFeedbackQuery.data.totals.avgRating != null
                          ? opdFeedbackQuery.data.totals.avgRating
                          : '—',
                    },
                    { label: '1', value: opdFeedbackQuery.data.totals.rating1 },
                    { label: '2', value: opdFeedbackQuery.data.totals.rating2 },
                    { label: '3', value: opdFeedbackQuery.data.totals.rating3 },
                    { label: '4–5', value: opdFeedbackQuery.data.totals.rating4 + opdFeedbackQuery.data.totals.rating5 },
                  ].map((t) => (
                    <div key={t.label} className="rounded-lg border p-3">
                      <p className="text-xs text-muted-foreground">{t.label}</p>
                      <p className="text-xl font-semibold tabular-nums">
                        {typeof t.value === 'number' ? t.value.toLocaleString() : t.value}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {opdFeedbackQuery.isLoading && (
                <p className="text-sm text-muted-foreground">Loading OPD feedback…</p>
              )}

              {!opdFeedbackQuery.isLoading && (
                <div className="overflow-x-auto rounded border">
                  <table className="w-full text-sm min-w-[900px]">
                    <thead className="bg-muted/40">
                      <tr className="text-left">
                        <th className="p-2 font-medium">Received</th>
                        <th className="p-2 font-medium">Patient</th>
                        <th className="p-2 font-medium">Phone</th>
                        <th className="p-2 font-medium text-right">Rating</th>
                        <th className="p-2 font-medium">Comment</th>
                        <th className="p-2 font-medium">Survey</th>
                      </tr>
                    </thead>
                    <tbody>
                      {opdFeedbackRows.map((r) => (
                        <tr key={r.id} className="border-t align-top">
                          <td className="p-2 whitespace-nowrap">{fmtReportTime(r.receivedAt)}</td>
                          <td className="p-2">
                            <div className="font-medium">{r.patientName || '—'}</div>
                            {r.leadId && (
                              <div className="text-xs text-muted-foreground font-mono">{r.leadId.slice(0, 8)}…</div>
                            )}
                          </td>
                          <td className="p-2 whitespace-nowrap tabular-nums">{r.phone}</td>
                          <td className="p-2 text-right tabular-nums font-medium">
                            {r.rating != null ? `${r.rating}/5` : '—'}
                          </td>
                          <td className="p-2 max-w-md">
                            <div className="whitespace-pre-wrap break-words">{r.freeText || '—'}</div>
                          </td>
                          <td className="p-2 text-xs text-muted-foreground">
                            {r.surveyName || r.templateName || '—'}
                          </td>
                        </tr>
                      ))}
                      {opdFeedbackRows.length === 0 && (
                        <tr>
                          <td colSpan={6} className="p-4 text-sm text-muted-foreground">
                            No Flow survey responses in this range yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
          </div>
        </TabsContent>

        <TabsContent value="templates">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Meta-style template builder</CardTitle>
              </CardHeader>
              <CardContent id="wa-meta-template-builder" className="space-y-5">
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" disabled={syncMetaMut.isPending} onClick={() => syncMetaMut.mutate()}>
                    {syncMetaMut.isPending ? 'Syncing…' : 'Sync from Meta'}
                  </Button>
                  <Select value={selectedPackCategory} onValueChange={(v: any) => setSelectedPackCategory(v)}>
                    <SelectTrigger className="w-[220px]">
                      <SelectValue placeholder="Select template pack" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AESTHETIC">Aesthetic Surgery Pack</SelectItem>
                      <SelectItem value="ORTHO">Ortho Pack (coming soon)</SelectItem>
                      <SelectItem value="MATERNITY">Maternity Pack</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    disabled={importAestheticPackMut.isPending}
                    onClick={() => importAestheticPackMut.mutate()}
                  >
                    {importAestheticPackMut.isPending ? 'Importing…' : `Import ${selectedPackCategory.toLowerCase()} pack`}
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Template name</Label>
                    <Input
                      value={metaTplForm.name}
                      onChange={(e) => setMetaTplForm((p) => ({ ...p, name: e.target.value }))}
                      placeholder="aesthetic_consultation_invite"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Language</Label>
                    <Input
                      value={metaTplForm.languageCode}
                      onChange={(e) => setMetaTplForm((p) => ({ ...p, languageCode: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select
                      value={metaTplForm.category}
                      onValueChange={(v) => setMetaTplForm((p) => ({ ...p, category: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MARKETING">MARKETING</SelectItem>
                        <SelectItem value="UTILITY">UTILITY</SelectItem>
                        <SelectItem value="AUTHENTICATION">AUTHENTICATION</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Body params count</Label>
                    <Input
                      type="number"
                      min={0}
                      value={metaTplForm.bodyParamCount}
                      onChange={(e) =>
                        setMetaTplForm((p) => ({ ...p, bodyParamCount: Number(e.target.value || 0) }))
                      }
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Header type</Label>
                    <Select
                      value={metaTplForm.headerType}
                      onValueChange={(v: any) => setMetaTplForm((p) => ({ ...p, headerType: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NONE">NONE</SelectItem>
                        <SelectItem value="TEXT">TEXT</SelectItem>
                        <SelectItem value="IMAGE">IMAGE</SelectItem>
                        <SelectItem value="VIDEO">VIDEO</SelectItem>
                        <SelectItem value="DOCUMENT">DOCUMENT</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Header text (if TEXT)</Label>
                    <Input
                      value={metaTplForm.headerText}
                      onChange={(e) => setMetaTplForm((p) => ({ ...p, headerText: e.target.value }))}
                      placeholder="Consultation update"
                    />
                  </div>
                </div>

                {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(metaTplForm.headerType) && (
                  <div className="rounded-md border border-dashed border-teal-200 bg-teal-50/40 p-3 space-y-2">
                    <Label>Header media (required for Meta approval)</Label>
                    <p className="text-xs text-muted-foreground">
                      Use a <strong>direct HTTPS URL</strong> to the file (e.g.{' '}
                      <code>https://your-bucket.s3.amazonaws.com/intro.mp4</code>).{' '}
                      <strong>Google Drive / OneDrive links do not work</strong> (they return HTML).
                      Small images (&lt;4MB) can use file upload.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Input
                        className="flex-1 min-w-[200px]"
                        placeholder="https://cdn.example.com/intro-aesthetics.mp4"
                        value={headerMediaUrlInput}
                        onChange={(e) => setHeaderMediaUrlInput(e.target.value)}
                      />
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={
                          uploadTemplateMediaFromUrlMut.isPending ||
                          !headerMediaUrlInput.trim().startsWith('https://')
                        }
                        onClick={() => uploadTemplateMediaFromUrlMut.mutate()}
                      >
                        {uploadTemplateMediaFromUrlMut.isPending ? 'Uploading…' : 'Upload from URL'}
                      </Button>
                    </div>
                    <Input
                      type="file"
                      accept={
                        metaTplForm.headerType === 'VIDEO'
                          ? 'video/mp4,video/*'
                          : metaTplForm.headerType === 'DOCUMENT'
                            ? 'application/pdf,.pdf'
                            : 'image/jpeg,image/png,image/webp'
                      }
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        if (f.size > 4 * 1024 * 1024) {
                          toast.error(
                            `File is ${(f.size / (1024 * 1024)).toFixed(1)}MB — use Upload from URL instead (nginx limit).`
                          );
                          e.target.value = '';
                          return;
                        }
                        uploadTemplateMediaMut.mutate(f);
                      }}
                    />
                    {uploadTemplateMediaMut.isPending && (
                      <p className="text-xs text-teal-700">Uploading to Meta…</p>
                    )}
                    {metaTplForm.headerMediaHandle && (
                      <p className="text-xs text-green-700">
                        Meta handle ready: {metaTplForm.headerMediaFilename || 'media'}
                      </p>
                    )}
                    {metaTplForm.headerMediaId && !metaTplForm.headerMediaHandle && (
                      <p className="text-xs text-amber-800">
                        WhatsApp media id saved for campaigns. Meta template submit still needs handle
                        upload (check WA_META_APP_ID on server).
                      </p>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Body</Label>
                  <Textarea
                    value={metaTplForm.bodyText}
                    onChange={(e) => setMetaTplForm((p) => ({ ...p, bodyText: e.target.value }))}
                    placeholder="Hi {{1}}, thank you for contacting {{2}}..."
                    rows={4}
                  />
                  {(() => {
                    const detected = detectMetaBodyParamMax(metaTplForm.bodyText);
                    const configured = Number(metaTplForm.bodyParamCount || 0);
                    if (!detected && !configured) return null;
                    const mismatch = detected && configured && detected !== configured;
                    return (
                      <div className="text-xs text-slate-600 flex flex-wrap items-center gap-2">
                        <span>
                          Detected in body: <strong>{detected}</strong> param(s)
                          {configured ? (
                            <>
                              {' '}
                              · configured: <strong>{configured}</strong>
                            </>
                          ) : null}
                        </span>
                        {mismatch ? (
                          <span className="text-amber-700">
                            (Mismatch: Meta expects the highest index used in the body.)
                          </span>
                        ) : null}
                        {detected !== configured ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setMetaTplForm((p) => ({ ...p, bodyParamCount: detected }))
                            }
                          >
                            Set count = {detected}
                          </Button>
                        ) : null}
                      </div>
                    );
                  })()}
                </div>

                {Math.max(Number(metaTplForm.bodyParamCount || 0), detectMetaBodyParamMax(metaTplForm.bodyText)) >
                0 ? (
                  <div className="rounded-md border bg-white p-3 space-y-3">
                    <div className="text-xs font-semibold text-slate-700">
                      Param guide (for <code>{'{{1}}'}</code> variables)
                    </div>
                    <p className="text-xs text-slate-600">
                      In the template body, Meta variables look like <code>{'{{1}}'}</code>,{' '}
                      <code>{'{{2}}'}</code>, … (numbers only). Use this section to note what each variable
                      means so staff don’t get confused later.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {Array.from({
                        length: Math.max(
                          Number(metaTplForm.bodyParamCount || 0),
                          detectMetaBodyParamMax(metaTplForm.bodyText)
                        ),
                      }).map((_, idx) => {
                        const i = idx + 1;
                        return (
                          <div key={i} className="space-y-1">
                            <Label className="text-xs">Param {i} meaning</Label>
                            <Input
                              value={metaTplParamNotes[i] ?? ''}
                              onChange={(e) =>
                                setMetaTplParamNotes((p) => ({ ...p, [i]: e.target.value }))
                              }
                              placeholder={`e.g. Patient name, Doctor name, Appointment date…`}
                            />
                          </div>
                        );
                      })}
                    </div>

                    <div className="pt-2 border-t">
                      <div className="text-xs font-semibold text-slate-700 mb-2">
                        Available tokens (for campaigns / auto-fill)
                      </div>
                      <p className="text-xs text-slate-600 mb-2">
                        When you create a campaign, you can pass body params that use tokens like{' '}
                        <code>{'{{lead.patientName}}'}</code>. The system replaces them per lead.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {AVAILABLE_CAMPAIGN_TOKENS.map((t) => (
                          <span
                            key={t.token}
                            className="inline-flex items-center gap-2 rounded-full border bg-slate-50 px-2 py-1 text-xs"
                            title={t.meaning}
                          >
                            <code className="font-mono">{t.token}</code>
                            <span className="text-slate-500">— {t.meaning}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : null}
                <div className="space-y-2">
                  <Label>Footer (optional)</Label>
                  <Input
                    value={metaTplForm.footerText}
                    onChange={(e) => setMetaTplForm((p) => ({ ...p, footerText: e.target.value }))}
                    placeholder="Reply STOP to opt out"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Buttons (optional, comma separated labels)</Label>
                  <Input
                    value={metaTplForm.buttonsCsv}
                    onChange={(e) => setMetaTplForm((p) => ({ ...p, buttonsCsv: e.target.value }))}
                    placeholder="Book now, Talk to coordinator"
                  />
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setMetaTplForm((p) => ({
                        ...p,
                        bodyText: appendWelcomeUtmToTemplateBody(p.bodyText),
                      }));
                      toast.success('Added WhatsApp tracking UTMs to links in body');
                    }}
                  >
                    Add website tracking UTMs to links
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    Uses utm_source={WHATSAPP_CAMPAIGN_WELCOME_UTM.utm_source}, utm_medium=
                    {WHATSAPP_CAMPAIGN_WELCOME_UTM.utm_medium}, utm_content per link (book_appointment,
                    press_releases, home). Re-submit template to Meta after editing links.
                  </p>
                </div>

                <div className="rounded-md border bg-slate-50 p-3">
                  <div className="text-xs font-semibold text-slate-700 mb-2">Template preview</div>
                  {metaTplForm.headerType === 'TEXT' && metaTplForm.headerText.trim() && (
                    <div className="text-sm font-medium mb-1">{metaTplForm.headerText}</div>
                  )}
                  <div className="text-sm whitespace-pre-wrap">{metaTplForm.bodyText || 'Body preview...'}</div>
                  {metaTplForm.footerText.trim() && (
                    <div className="text-xs text-muted-foreground mt-2">{metaTplForm.footerText}</div>
                  )}
                  {!!metaTplForm.buttonsCsv.trim() && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {metaTplForm.buttonsCsv
                        .split(/[,;\n]+/)
                        .map((x) => x.trim())
                        .filter(Boolean)
                        .slice(0, 3)
                        .map((btn) => (
                          <span key={btn} className="rounded-full border px-2 py-1 text-xs bg-white">
                            {btn}
                          </span>
                        ))}
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    disabled={createTemplateMut.isPending || !metaTplForm.name.trim()}
                    onClick={() =>
                      createTemplateMut.mutate({
                        name: metaTplForm.name.trim(),
                        languageCode: metaTplForm.languageCode.trim() || 'en',
                        category: metaTplForm.category,
                        bodyParamCount: metaTplForm.bodyParamCount,
                        description: 'Saved from Meta-style builder',
                        status: 'DRAFT',
                        metaSchema: {
                          headerType: metaTplForm.headerType,
                          headerText: metaTplForm.headerText,
                          bodyText: metaTplForm.bodyText,
                          footerText: metaTplForm.footerText,
                          buttons: metaTplForm.buttonsCsv
                            .split(/[,;\n]+/)
                            .map((x) => x.trim())
                            .filter(Boolean),
                        },
                      } as any)
                    }
                  >
                    Save local draft
                  </Button>
                  <Button
                    disabled={
                      createMetaTemplateMut.isPending ||
                      !metaTplForm.name.trim() ||
                      !metaTplForm.bodyText.trim() ||
                      (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(metaTplForm.headerType) &&
                        !metaTplForm.headerMediaHandle.trim())
                    }
                    title={
                      ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(metaTplForm.headerType) &&
                      !metaTplForm.headerMediaHandle.trim()
                        ? 'Upload header media first (Meta approval handle)'
                        : undefined
                    }
                    onClick={() => {
                      if (hasLeadingOrTrailingParam(metaTplForm.bodyText)) {
                        toast.error(
                          "Meta rule: Variables ({{1}}) can’t be at the start or end of the BODY. Add text before/after the variable (e.g., 'Hi {{1}}, ...' and end with a '.' )."
                        );
                        return;
                      }
                      createMetaTemplateMut.mutate();
                    }}
                  >
                    {createMetaTemplateMut.isPending ? 'Submitting…' : 'Submit to Meta'}
                  </Button>
                  <Button
                    disabled={
                      createMetaTemplateAndSyncMut.isPending ||
                      !metaTplForm.name.trim() ||
                      !metaTplForm.bodyText.trim() ||
                      (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(metaTplForm.headerType) &&
                        !metaTplForm.headerMediaHandle.trim())
                    }
                    onClick={() => {
                      if (hasLeadingOrTrailingParam(metaTplForm.bodyText)) {
                        toast.error(
                          "Meta rule: Variables ({{1}}) can’t be at the start or end of the BODY. Add text before/after the variable (e.g., 'Hi {{1}}, ...' and end with a '.' )."
                        );
                        return;
                      }
                      createMetaTemplateAndSyncMut.mutate();
                    }}
                  >
                    {createMetaTemplateAndSyncMut.isPending ? 'Working…' : 'Submit + sync status'}
                  </Button>
                </div>
                <div className="text-xs text-muted-foreground">
                  Approval is still done by Meta. This builder mirrors Meta flow and keeps your local repository synchronized.
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Templates</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 mb-4">
                  <Input
                    value={templateSearch}
                    onChange={(e) => setTemplateSearch(e.target.value)}
                    placeholder="Search by name, category, description..."
                  />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <Select value={templateStatusFilter} onValueChange={(v: any) => setTemplateStatusFilter(v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL">All statuses</SelectItem>
                        <SelectItem value="DRAFT">Draft</SelectItem>
                        <SelectItem value="ACTIVE">Active</SelectItem>
                        <SelectItem value="INACTIVE">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={templateCategoryFilter} onValueChange={(v: any) => setTemplateCategoryFilter(v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL">All categories</SelectItem>
                        <SelectItem value="MARKETING">MARKETING</SelectItem>
                        <SelectItem value="UTILITY">UTILITY</SelectItem>
                        <SelectItem value="AUTHENTICATION">AUTHENTICATION</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select value={templateLanguageFilter} onValueChange={setTemplateLanguageFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="Language" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL">All languages</SelectItem>
                        {availableLanguages.map((lang) => (
                          <SelectItem key={lang} value={lang}>
                            {lang}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {templatesLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
                {!templatesLoading && templates.length === 0 && (
                  <div className="text-sm text-muted-foreground">No templates yet.</div>
                )}
                {!templatesLoading && templates.length > 0 && filteredTemplates.length === 0 && (
                  <div className="text-sm text-muted-foreground">No templates match current filters.</div>
                )}
                {!templatesLoading && filteredTemplates.length > 0 && (
                  <div className="space-y-2">
                    {filteredTemplates.slice(0, 24).map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => loadTemplateIntoBuilder(t)}
                        className="w-full text-left rounded-md border p-3 hover:bg-slate-50 active:bg-slate-100 transition-colors cursor-pointer"
                      >
                        <div className="font-medium">{t.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {t.languageCode} · params: {t.bodyParamCount} · {t.status}
                        </div>
                        {metaTplFormLoadedFrom === t.id && (
                          <div className="mt-2 text-xs text-blue-700 font-medium">Loaded into builder</div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="button-rules">
          <Card>
            <CardHeader>
              <CardTitle>Button click → CRM status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                When a patient taps a template quick-reply (e.g. &quot;Interested&quot;), THMS updates the lead
                status from these rules. Configure webhook + Mini CRM WhatsApp number first.
              </p>
              <div className="flex gap-2">
                <Button variant="outline" disabled={seedButtonRulesMut.isPending} onClick={() => seedButtonRulesMut.mutate()}>
                  {seedButtonRulesMut.isPending ? 'Seeding…' : 'Add default rules (Interested, YES, Book now)'}
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end border-t pt-4">
                <div className="space-y-2 md:col-span-1">
                  <Label>Button label (exact)</Label>
                  <Input
                    value={buttonRuleForm.buttonLabel}
                    onChange={(e) => setButtonRuleForm((p) => ({ ...p, buttonLabel: e.target.value }))}
                    placeholder="Interested"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Lead status</Label>
                  <Input
                    value={buttonRuleForm.leadStatus}
                    onChange={(e) => setButtonRuleForm((p) => ({ ...p, leadStatus: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sub-status</Label>
                  <Input
                    value={buttonRuleForm.leadSubStatus}
                    onChange={(e) => setButtonRuleForm((p) => ({ ...p, leadSubStatus: e.target.value }))}
                  />
                </div>
                <Button
                  disabled={createButtonRuleMut.isPending || !buttonRuleForm.buttonLabel.trim()}
                  onClick={() => createButtonRuleMut.mutate()}
                >
                  Add rule
                </Button>
              </div>
              <div className="space-y-2">
                {buttonRules.length === 0 && (
                  <p className="text-sm text-muted-foreground">No rules yet.</p>
                )}
                {buttonRules.map((r) => (
                  <div key={r.id} className="flex justify-between items-center rounded border p-2 text-sm">
                    <span>
                      <strong>{r.buttonLabel}</strong> → {r.leadStatus || '—'}
                      {r.leadSubStatus ? ` / ${r.leadSubStatus}` : ''}
                      {!r.isActive && ' (inactive)'}
                    </span>
                    <span className="text-xs text-muted-foreground">priority {r.priority}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

